// NOOK – configurateur 3D de meubles sur mesure (Three.js).
// Le client part du modèle de base (reproduit d'après la photo de la fiche) et décide de tout :
// dimensions, colonnes, largeurs, portes ou non, tiroirs, étagères, plan de travail, finitions.
// Prix = prix du modèle présenté + / − chaque changement, détaillé ligne par ligne.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/controls/OrbitControls.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/environments/RoomEnvironment.js/+esm';

const root = document.getElementById('nk-config');
if (root) init(root);

function init(root) {
  let BASE = +root.dataset.base || 1490;
  const KREF = +root.dataset.kref || 0; // prix du Monolithe, pour caler les tarifs du meuble vierge
  const DEMO = root.dataset.demo === '1'; // site parallèle : pas de panier Shopify
  const HANDLE = (root.dataset.handle || '').replace('meuble-sous-escalier-', '');
  const NAME = root.dataset.name || 'Meuble';
  const SHAPE = root.dataset.shape || 'escalier'; // 'escalier' (sous pente) ou 'droit' (bibliothèque, dressing…)
  const DISC = root.dataset.discount === undefined ? 10 : +root.dataset.discount || 0; // remise automatique de la boutique (%)
  const DELAI = root.dataset.delai || '6 à 7 semaines';
  const IMAGE = root.dataset.image || '';
  const afterDisc = t => Math.round(t * (100 - DISC) / 100);

  // ---------- Catalogue ----------
  // Décors mélaminés du fournisseur (nuancier Moganshan envoyé les 18 et 19/10) : même prix au m², d'où t: 0 partout.
  // ref = nom du modèle chez le fournisseur, repris dans le récapitulatif de commande.
  const COLORS = [
    { id: 'blanc-lune', n: 'Blanc lune', ref: '月光白', c: '#F2F2EF', t: 0 },
    { id: 'blanc', n: 'Blanc', ref: 'J01白色', c: '#F1EEE8', t: 0 },
    { id: 'blanc-casse', n: 'Blanc cassé', ref: '云巷白', c: '#EEE9DF', t: 0 },
    { id: 'ivoire', n: 'Ivoire', ref: '夏特白', c: '#EFE9DB', t: 0 },
    { id: 'creme', n: 'Crème', ref: '杏雨念芯', c: '#F3E8D3', t: 0 },
    { id: 'creme-lait', n: 'Crème lait', ref: '奶油白', c: '#EDE5D7', t: 0 },
    { id: 'rose', n: 'Rose poudré', ref: '胭脂粉', c: '#E6DAD0', t: 0 },
    { id: 'grege', n: 'Grège', ref: 'échantillon photo 7 (19/10)', c: '#CBC1B4', t: 0 },
    { id: 'taupe', n: 'Taupe', ref: '摩登灰', c: '#C9C0B3', t: 0 },
    { id: 'gris', n: 'Gris', ref: 'échantillon photo 10 (19/10)', c: '#9A978F', t: 0 },
    { id: 'hetre', n: 'Hêtre clair', ref: 'échantillon photos 2 et 6 (19/10)', c: '#D4BEA1', t: 0, wood: true },
    { id: 'chene-naturel', n: 'Chêne naturel', ref: 'échantillon photo 14 gauche (18/10)', c: '#D0C09B', t: 0, wood: true },
    { id: 'chene-miel', n: 'Chêne miel', ref: 'échantillon photos 3 et 4 (19/10)', c: '#B08A72', t: 0, wood: true },
    { id: 'noyer-dore', n: 'Noyer doré', ref: '凝香胡桃', c: '#997260', t: 0, wood: true },
    { id: 'noyer-chaud', n: 'Noyer chaud', ref: '云垂海立', c: '#8E624D', t: 0, wood: true },
    { id: 'teck', n: 'Teck', ref: '茶香板', c: '#90614D', t: 0, wood: true },
    { id: 'noyer', n: 'Noyer', ref: '花满九州', c: '#735348', t: 0, wood: true },
    { id: 'merisier', n: 'Merisier', ref: 'échantillon photo 14 milieu (18/10)', c: '#6C3D22', t: 0, wood: true },
    { id: 'noyer-fonce', n: 'Noyer foncé', ref: 'échantillon photo 9 (19/10)', c: '#69493D', t: 0, wood: true },
    { id: 'chene-fume', n: 'Chêne fumé', ref: '醉挽清风', c: '#5D5249', t: 0, wood: true },
    { id: 'wenge', n: 'Wengé', ref: '浅棕梨木', c: '#4E3427', t: 0, wood: true },
  ];
  const TIERS = ['Uni', 'Bois', 'Laqué'];
  const TIER_MULT = [1, 1.12, 1.28];
  const HANDLES = [
    { id: 'push', n: 'Sans poignée (push)', p: 12, c: null },
    { id: 'noir', n: 'Barre noir mat', p: 9, c: '#1a1a1a' },
    { id: 'laiton', n: 'Barre laiton', p: 19, c: '#B8975E' },
    { id: 'chrome', n: 'Barre chrome', p: 9, c: '#C9CACC' },
    { id: 'bouton-laiton', n: 'Bouton laiton', p: 14, c: '#B8975E', knob: true },
    { id: 'bouton-noir', n: 'Bouton noir', p: 10, c: '#222', knob: true },
  ];
  const TOPS = [
    { id: 'struct', n: 'Comme la structure', p: 0 },
    { id: 'blanc', n: 'Blanc', c: '#F3F1EC', p: 0 },
    { id: 'pierre', n: 'Pierre claire', c: '#D9D3C8', p: 70, stone: true },
    { id: 'marbre', n: 'Marbre blanc', c: '#F1EFEB', p: 140, marble: true },
    { id: 'noir', n: 'Noir', c: '#262524', p: 0 },
  ];
  const FRAMES = [
    { id: 'front', n: 'Comme les façades', p: 0 },
    { id: 'noir', n: 'Noir', c: '#1c1c1c', p: 0 },
    { id: 'laiton', n: 'Laiton', c: '#B8975E', p: 18, metal: true },
  ];
  const STYLES = [{ id: 'lisse', n: 'Lisses' }, { id: 'cadre', n: 'À cadre (classique)' }];
  const FACADES = [
    { id: 'ouvert', n: 'Aucune' },
    { id: 'porte', n: 'Porte' },
    { id: 'double', n: 'Double porte' },
    { id: 'verre', n: 'Porte vitrée' },
    { id: 'coulissant', n: 'Coulissante' },
  ];
  const SOCLES = [
    { id: 'aucun', n: 'Aucun', h: 0 },
    { id: 'plinthe', n: 'Plinthe 8 cm', h: 80 },
    { id: 'pieds', n: 'Pieds métal 12 cm', h: 120 },
  ];
  // Tarifs unitaires (avant calage sur le prix du modèle présenté)
  const PRICE = {
    structM2: 230, backM2: 28, shelf: 38, glassShelf: 74, door: 150, doorMin: 70, glass: 280, glassMin: 120,
    doubleExtra: 25, div: 45, drawer: 92, slide: 165, rod: 34, counterM: 160, led: 95, plintheM: 38, piedsM: 60,
  };
  const LIM = { L: [800, 6000], F: [0, 3000], H: [600, 3200], h: [300, 2200], D: [250, 900], colW: [250, 1200] };

  // ---------- Modèles de base, reproduits d'après la photo principale de chaque fiche ----------
  // Colonne : façade du bas, tiroirs en bas, plan de travail à 90 cm, et au-dessus une niche ou un placard haut.
  const C = (facade, o = {}) => ({ w: 1, facade, upper: 'ouvert', hinge: 'g', drawers: 0, shelves: 3, glassShelves: false, rod: false, led: false, counter: false, ...o });
  // stair : décor de l'escalier autour du meuble (limon, marches, garde-corps), pour coller à la photo
  const PRESETS = {
    monolithe: { dims: { L: 3400, F: 250, H: 2600, h: 600, dir: 'droite' }, body: 'ivoire', front: 'ivoire', inside: 'ivoire', handle: 'laiton', socle: 'plinthe', style: 'lisse',
      stair: { limon: '#ECE6DA', marche: '#8A5A33', rampe: 'balustres-noir' },
      cols: Array.from({ length: 7 }, (_, i) => C('porte', { hinge: i % 2 ? 'd' : 'g', shelves: 4 })) },
    coulisse: { dims: { L: 3000, F: 300, H: 2400, h: 400, dir: 'gauche' }, body: 'chene-naturel', front: 'chene-naturel', inside: 'chene-naturel', handle: 'push', socle: 'aucun', style: 'lisse',
      stair: { limon: '#F2F0EB', marche: '#F2F0EB', rampe: 'verre', closed: true },
      cols: [C('porte', { shelves: 1 }), C('ouvert', { shelves: 3 }), C('coulissant', { shelves: 4 }), C('ouvert', { shelves: 6 }), C('porte', { hinge: 'd', shelves: 5 })] },
    // photo : limon anthracite plein sous garde-corps vitré, colonne blanche à gauche, grande niche sombre
    // en casiers éclairés de hauteurs différentes, plan sombre, tiroirs blancs, porte blanche en triangle à droite
    galerie: { dims: { L: 3300, F: 450, H: 2600, h: 750, dir: 'droite' }, body: 'chene-fume', front: 'blanc', inside: 'chene-fume', top: 'struct', handle: 'noir', socle: 'plinthe', style: 'lisse',
      floor: 'gris', stair: { limon: '#47474A', marche: '#BDBAB4', rampe: 'verre', closed: true },
      cols: [C('porte', { w: 0.5, shelves: 4 }), C('ouvert', { w: 1.15, counter: true, drawers: 3, shelves: 3, div: 2, led: true }),
        C('ouvert', { w: 1.2, counter: true, drawers: 2, shelves: 3, div: 2, led: true }), C('porte', { w: 0.85, shelves: 1 })] },
    // photo : plan de travail pierre sur toute la largeur, une seule grande niche éclairée au-dessus,
    // dessous 2 blocs de 3 tiroirs, une cave vitrée noire et une porte étroite ; socle éclairé
    'le-comptoir': { dims: { L: 2050, F: 100, H: 2950, h: 950, dir: 'gauche' }, body: 'chene-miel', front: 'chene-miel', inside: 'ivoire', shelfc: 'chene-miel', top: 'pierre', frame: 'noir',
      handle: 'noir', socle: 'plinthe', ledSocle: true, merge: true, style: 'lisse',
      stair: { limon: '#B5844F', marche: '#EEEAE3', rampe: 'verre', led: true, closed: true },
      cols: [C('ouvert', { w: 1.32, counter: true, drawers: 3, shelves: 3, led: true }), C('ouvert', { w: 1.38, counter: true, drawers: 3, shelves: 3, led: true }),
        C('verre', { w: 1.1, counter: true, shelves: 4, led: true }), C('porte', { w: 0.85, counter: true, hinge: 'd', shelves: 4, led: true })] },
    vitrine: { dims: { L: 3600, F: 850, H: 2600, h: 800, dir: 'droite' }, body: 'blanc', front: 'blanc', inside: 'noyer', frame: 'noir', handle: 'push', socle: 'aucun', style: 'lisse',
      stair: { limon: '#F2F0EB', marche: '#A99A86', rampe: 'aucune' },
      cols: [C('verre', { w: 2.2, shelves: 4, led: true }), C('porte'), C('porte', { hinge: 'd' }), C('ouvert', { shelves: 3, led: true }),
        C('porte'), C('porte', { hinge: 'd' }), C('ouvert', { shelves: 1, led: true }), C('porte')] },
    salon: { dims: { L: 3000, F: 700, H: 2900, h: 900, dir: 'droite' }, body: 'blanc', front: 'blanc', inside: 'noyer', frame: 'noir', handle: 'push', socle: 'aucun', style: 'lisse',
      stair: { limon: '#7A5235', marche: 'marbre', rampe: 'aucune' },
      cols: [C('ouvert', { counter: true, drawers: 3, upper: 'verre', shelves: 2 }), C('porte', { counter: true, shelves: 3 }), C('porte', { counter: true, hinge: 'd', shelves: 2 }),
        C('porte', { w: 0.8 }), C('porte', { w: 0.8, hinge: 'd' }), C('porte', { w: 0.8 })] },
    haussmann: { dims: { L: 3200, F: 500, H: 2800, h: 1500, dir: 'gauche' }, body: 'ivoire', front: 'ivoire', inside: 'ivoire', top: 'marbre', handle: 'bouton-laiton', socle: 'plinthe', style: 'cadre',
      stair: { limon: '#F2F0EB', marche: '#9A6A44', rampe: 'balustres-blanc' },
      cols: [C('double', { w: 1.1, shelves: 3 }), C('ouvert', { counter: true, drawers: 3, upper: 'porte', shelves: 1 }), C('porte', { counter: true, drawers: 1, upper: 'verre', shelves: 3, led: true }),
        C('porte', { counter: true, drawers: 1, hinge: 'd', upper: 'verre', shelves: 3, led: true }), C('porte', { w: 0.7, hinge: 'd', shelves: 5 })] },
    heritage: { dims: { L: 2600, F: 300, H: 2500, h: 450, dir: 'gauche' }, body: 'ivoire', front: 'ivoire', inside: 'ivoire', handle: 'bouton-noir', socle: 'plinthe', style: 'cadre',
      stair: { limon: '#ECE6DA', marche: '#6E4227', rampe: 'balustres-blanc' },
      cols: [C('porte', { drawers: 6, shelves: 0 }), C('porte', { drawers: 6, shelves: 0 }), C('porte', { drawers: 6, shelves: 0 }), C('porte', { drawers: 6, shelves: 1 }), C('porte', { w: 0.6, hinge: 'd', shelves: 4 })] },
    nordique: { dims: { L: 3200, F: 900, H: 2700, h: 500, dir: 'droite' }, body: 'chene-naturel', front: 'grege', inside: 'chene-naturel', top: 'blanc', handle: 'push', socle: 'plinthe', style: 'lisse',
      stair: { limon: '#CDA676', marche: '#CDA676', rampe: 'verre' },
      cols: [C('porte', { counter: true, upper: 'porte', shelves: 1, led: true }), C('porte', { counter: true, upper: 'porte', hinge: 'd', shelves: 1, led: true }),
        C('porte', { counter: true, upper: 'porte', shelves: 1, led: true }), C('porte', { counter: true, hinge: 'd', shelves: 1, led: true }), C('porte', { shelves: 2 }), C('porte', { hinge: 'd', shelves: 1 })] },
    // « Créer de zéro » : un meuble vierge, quatre colonnes d'étagères ouvertes, que le client aménage lui-même
    zero: { dims: { L: 3000, F: 300, H: 2500, h: 600, dir: 'droite' }, body: 'blanc', front: 'blanc', inside: 'blanc', handle: 'push', socle: 'plinthe', style: 'lisse',
      stair: { limon: '#F2F0EB', marche: '#C9A57A', rampe: 'verre' },
      cols: Array.from({ length: 4 }, () => C('ouvert', { shelves: 3 })) },
  };
  const pre = PRESETS[HANDLE] || PRESETS.monolithe;
  const STAIR = pre.stair || { limon: '#F2F0EB', marche: '#B98A5C', rampe: 'verre' };
  function baseState(p = pre) {
    const s = {
      L: 3000, F: 900, H: 2400, h: 700, D: 600, dir: 'droite', ...p.dims,
      cols: clone(p.cols),
      body: p.body, front: p.front, inside: p.inside || p.body, shelfc: p.shelfc || p.inside || p.body, top: p.top || 'struct', frame: p.frame || 'front',
      style: p.style || 'lisse', handle: p.handle, socle: p.socle, back: true, merge: !!p.merge, ledSocle: !!p.ledSocle,
    };
    if (SHAPE === 'droit') { s.F = s.L; s.h = s.H; }
    const tw = s.cols.reduce((a, c) => a + c.w, 0);
    s.cols.forEach(c => (c.w = s.L * c.w / tw));
    return s;
  }
  let S = baseState();
  let sel = 0, open = false, cotes = null; // cotes : null = affichées seulement sur l'onglet Dimensions
  const past = [], future = [];
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function commit(fn) { past.push(clone(S)); if (past.length > 60) past.shift(); future.length = 0; fn(); normalize(); render(); }

  // ---------- Géométrie ----------
  const T = 18;
  const baseH = st => (SOCLES.find(x => x.id === st.socle) || SOCLES[0]).h;
  function hAt(st, x) {
    if (SHAPE === 'droit') return st.H;
    const xx = st.dir === 'droite' ? x : st.L - x;
    if (xx <= st.F) return st.H;
    return st.H - ((xx - st.F) / Math.max(1, st.L - st.F)) * (st.H - st.h);
  }
  // portion [a, b] de [xa, xb] où il reste au moins `need` mm sous la pente au-dessus de y
  function spanUnder(st, y, xa, xb, need) {
    const yy = y + need;
    if (SHAPE === 'droit' || yy <= st.h) return [xa, xb];
    if (yy > st.H) return null;
    const xc = st.F + (st.H - yy) / Math.max(1, st.H - st.h) * (st.L - st.F);
    const r = st.dir === 'droite' ? [xa, Math.min(xb, xc)] : [Math.max(xa, st.L - xc), xb];
    return r[1] - r[0] > 120 ? r : null;
  }
  function columns(st) {
    let x = 0;
    return st.cols.map((k, i) => {
      const x0 = x, x1 = x + k.w; x = x1;
      const h0 = hAt(st, x0), h1 = hAt(st, x1), hm = hAt(st, (x0 + x1) / 2);
      return { i, k, x0, x1, w: k.w, h0, h1, hmin: Math.min(h0, h1, hm), hmax: Math.max(h0, h1, hm) };
    });
  }
  const colArea = c => c.w * (c.h0 + c.h1) / 2 / 1e6;
  const area = st => columns(st).reduce((a, c) => a + colArea(c), 0);
  const drawerH = (st, c) => Math.min(220, Math.max(120, (c.hmin - baseH(st) - 2 * T) / 5));
  const maxDrawers = (st, c) => Math.max(0, Math.min(6, Math.floor((c.hmin - baseH(st) - 2 * T - 120) / drawerH(st, c))));
  const UPPER_GAP = 480; // hauteur de la niche ouverte entre le plan de travail et le placard haut
  const COUNTER_MIN = 950; // hauteur minimale de colonne pour un plan de travail à 90 cm
  // colonnes voisines avec plan de travail et niche ouverte : une seule niche, sans séparation au-dessus du plan
  const mergedWithPrev = (st, cs, i) => !!st.merge && i > 0 && cs[i].k.counter && cs[i - 1].k.counter && cs[i].k.upper === 'ouvert' && cs[i - 1].k.upper === 'ouvert';
  // Découpage vertical d'une colonne, partagé par le prix et la 3D (ce qu'on voit = ce qu'on paie)
  function layout(st, c) {
    const k = c.k, base = baseH(st), yb = base + T, inTop = c.hmin - T - 6, avgTop = (c.h0 + c.h1) / 2 - T;
    const counterY = k.counter ? Math.max(yb + 300, 900 - 30) : null;
    const fill = counterY !== null && k.facade === 'ouvert' && k.drawers > 0;
    const dh = fill ? (counterY - yb) / k.drawers : drawerH(st, c);
    const yD = yb + k.drawers * dh + (k.drawers && !fill ? T : 0);
    const uy0 = counterY !== null ? counterY + 30 + UPPER_GAP : null;
    const upper = counterY !== null && k.upper !== 'ouvert' && inTop - uy0 > 250 ? { type: k.upper, y0: uy0 } : null;
    const lowerArea = k.facade === 'ouvert' ? 0 : c.w * Math.max(150, (counterY !== null ? counterY : avgTop) - yD) / 1e6;
    const upperArea = upper ? c.w * Math.max(150, avgTop - uy0) / 1e6 : 0;
    return { base, yb, inTop, dh, yD, fill, counterY, upper, lowerArea, upperArea };
  }
  const leaves = (type, w) => (type === 'double' ? 2 : type === 'verre' && w > 600 ? 2 : 1);

  // Garde la configuration toujours fabricable
  function normalize() {
    const st = S;
    for (const k of ['L', 'H', 'D']) st[k] = clamp(st[k], LIM[k][0], LIM[k][1]);
    if (SHAPE === 'droit') { st.F = st.L; st.h = st.H; }
    st.F = clamp(st.F, 0, st.L - 300); st.h = clamp(st.h, LIM.h[0], st.H);
    st.inside = st.inside || st.body; st.shelfc = st.shelfc || st.inside; st.top = st.top || 'struct'; st.frame = st.frame || 'front'; st.style = st.style || 'lisse';
    const n = Math.max(1, Math.min(10, Math.floor(st.L / LIM.colW[0]), st.cols.length));
    st.cols.length = n;
    // largeurs : on respecte les largeurs choisies, la différence est répartie sur les autres colonnes
    let tot = st.cols.reduce((s, k) => s + (k.w || 0), 0);
    if (Math.abs(tot - st.L) > 0.5) st.cols.forEach(k => (k.w = (k.w || st.L / n) * st.L / (tot || st.L)));
    st.cols.forEach(k => (k.w = clamp(k.w, LIM.colW[0], LIM.colW[1])));
    tot = st.cols.reduce((s, k) => s + k.w, 0);
    if (Math.abs(tot - st.L) > 0.5) { const f = st.L / tot; st.cols.forEach(k => (k.w *= f)); }
    sel = Math.min(sel, n - 1);
    columns(st).forEach(c => {
      const k = c.k;
      if (!k.upper) k.upper = 'ouvert';
      if (k.counter && c.hmin < COUNTER_MIN) k.counter = false;
      if (k.counter && k.facade === 'coulissant') k.facade = 'porte';
      if (k.facade === 'coulissant') k.drawers = 0;
      k.drawers = Math.min(k.drawers, maxDrawers(st, c));
      k.shelves = clamp(k.shelves, 0, 8); k.div = clamp(k.div || 0, 0, 3);
      if (c.w > 900 && k.facade === 'porte') k.facade = 'double';
    });
  }
  function setColWidth(i, w) {
    const st = S, n = st.cols.length; if (n < 2) return;
    w = clamp(w, LIM.colW[0], Math.min(LIM.colW[1], st.L - (n - 1) * LIM.colW[0]));
    const others = st.cols.filter((_, j) => j !== i), rest = st.L - w, sum = others.reduce((s, k) => s + k.w, 0);
    st.cols[i].w = w; others.forEach(k => (k.w = k.w * rest / sum));
  }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, +v || 0)); }

  // ---------- Prix ----------
  const colorOf = id => COLORS.find(c => c.id === id) || COLORS[0];
  const handleOf = id => HANDLES.find(h => h.id === id) || HANDLES[0];
  const socleOf = id => SOCLES.find(s => s.id === id) || SOCLES[0];
  const topOf = id => TOPS.find(t => t.id === id) || TOPS[0];
  const frameOf = id => FRAMES.find(f => f.id === id) || FRAMES[0];
  const styleOf = id => STYLES.find(s => s.id === id) || STYLES[0];
  const pl = (n, w, wp) => n + ' ' + (n > 1 ? wp || w + 's' : w);
  const eur = n => Math.round(n).toLocaleString('fr-FR') + ' €';
  const cm = mm => Math.round(mm / 10 * 10) / 10;
  const cmf = mm => String(cm(mm)).replace('.', ',');
  // Coûts réels du fournisseur, en USD (devis et grille de Lucy, octobre 2026). Les lignes « estimation » sont à confirmer.
  // Fabrication au m² de façade (longueur × hauteur, comme le fournisseur la calcule).
  // Transport (22/10/2026) : camion Chine → France DDP + livraison DHL/FedEx, 3,4 $/kg, 25 à 28 jours.
  // Poids estimé ≈ 45 kg par m² de façade (≈ 400 kg pour le Monolithe), soit ≈ 153 $/m². À recaler avec la liste de colisage de Lucy.
  // Ancien transport DDP de Lucy : ≈ 1 555 $ + 26,3 $/m².
  const SUP = {
    m2: 78,            // contreplaqué multicouche 18 mm + portes lisses (quincaillerie Higold comprise)
    m2Cadre: 98,       // portes à cadre / laquées / thermolaquées
    shipBase: 0, shipM2: 153,
    crateM2: 7,        // caisses en bois (12 $ pièce, ≈ 1 caisse pour 1,7 m²)
    safety: 100,       // marge de sécurité : le transport définitif n'est connu qu'après emballage
    glassM2: 28,       // porte vitrée, cadre alu noir ou doré
    led: 44,           // ruban 16 $ + transformateur 20 $ + interrupteur 8 $, par colonne
    slide: 120,        // panier ou colonne coulissante
    drawer: 15,        // coulisses à fermeture douce + caisson de tiroir (estimation)
    handle: 9, push: 5,// poignée longue ou usinée 9 $ ; ouverture push (estimation)
    straightener: 12,  // redresseur pour porte de plus de 1,6 m
    stoneM: 78,        // plan effet pierre ou marbre, par mètre
    glassShelf: 15, rod: 10, piedsM: 20, // estimations
  };
  const USD_EUR = 0.92, PUB = 300, PROFIT = 600, NET = 1 - 0.124 - 0.02; // Urssaf micro-entreprise 12,4 %, frais Shopify ≈ 2 %
  const K = USD_EUR / NET; // 1 $ de coût fournisseur → € de prix de vente
  const sale = usd => Math.ceil((usd * USD_EUR + PUB + PROFIT) / NET / 10) * 10;
  function cost(st) {
    const Hm = Math.max(st.H, st.h), A = st.L * Hm / 1e6, dF = Math.max(1, st.D / 600);
    const cadre = st.style === 'cadre', isPush = st.handle === 'push';
    const q = { glass: [0, 0], drawer: [0, 0], slide: [0, 0], led: [0, 0], handle: [0, 0], counter: [0, 0], gshelf: [0, 0], rod: [0, 0], straight: [0, 0] };
    const add = (k, n, p) => { q[k][0] += n; q[k][1] += p; };
    columns(st).forEach(c => {
      const k = c.k, lay = layout(st, c);
      const facade = (type, ar, hgt) => {
        if (type === 'ouvert') return;
        const n = type === 'coulissant' ? 1 : leaves(type, c.w);
        if (type === 'verre') add('glass', n, ar * SUP.glassM2);
        if (type === 'coulissant') add('slide', 1, SUP.slide);
        add('handle', n, n * (isPush ? SUP.push : SUP.handle));
        if (hgt > 1600 && type !== 'coulissant') add('straight', n, n * SUP.straightener);
      };
      const lowTop = lay.counterY !== null ? lay.counterY : (c.h0 + c.h1) / 2;
      facade(k.facade, lay.lowerArea, lowTop - lay.yD);
      if (lay.upper) facade(lay.upper.type, lay.upperArea, (c.h0 + c.h1) / 2 - lay.upper.y0);
      if (k.drawers) { add('drawer', k.drawers, k.drawers * SUP.drawer); add('handle', k.drawers, k.drawers * (isPush ? SUP.push : SUP.handle)); }
      if (k.glassShelves) { const nd = (k.counter || k.facade === 'ouvert') ? (k.div || 0) : 0; const n = k.shelves * (1 + nd); add('gshelf', n, n * SUP.glassShelf); }
      if (k.rod) add('rod', 1, SUP.rod);
      if (lay.counterY !== null) add('counter', 1, topOf(st.top).stone || topOf(st.top).marble ? c.w / 1000 * SUP.stoneM : 0);
      if (k.led) add('led', 1, SUP.led);
    });
    const a2 = A.toFixed(2).replace('.', ',');
    return [
      ['struct', `Meuble sur mesure ${cmf(st.L)} × ${cmf(Hm)} cm (${a2} m²), façades ${cadre ? 'à cadre' : 'lisses'}, ${pl(st.cols.length, 'colonne')}`, A * (cadre ? SUP.m2Cadre : SUP.m2) * dF],
      ['ship', 'Transport et livraison en France, taxes comprises', SUP.shipBase + SUP.safety + A * (SUP.shipM2 + SUP.crateM2)],
      ['glass', pl(q.glass[0], 'porte vitrée', 'portes vitrées'), q.glass[1]],
      ['drawer', pl(q.drawer[0], 'tiroir') + ' à fermeture douce', q.drawer[1]],
      ['slide', pl(q.slide[0], 'colonne coulissante', 'colonnes coulissantes'), q.slide[1]],
      ['led', 'Éclairage LED · ' + pl(q.led[0], 'colonne'), q.led[1]],
      ['ledsocle', 'Éclairage LED sous le socle', st.ledSocle && st.socle === 'plinthe' ? SUP.led : 0],
      ['handle', (isPush ? 'Ouverture sans poignée' : 'Poignées ' + handleOf(st.handle).n) + ' × ' + q.handle[0], q.handle[1]],
      ['counter', 'Plan de travail ' + topOf(st.top).n, q.counter[1]],
      ['gshelf', pl(q.gshelf[0], 'étagère en verre', 'étagères en verre'), q.gshelf[1]],
      ['rod', pl(q.rod[0], 'penderie'), q.rod[1]],
      ['straight', 'Redresseurs pour grandes portes × ' + q.straight[0], q.straight[1]],
      ['socle', socleOf(st.socle).n, st.socle === 'pieds' ? st.L / 1000 * SUP.piedsM : 0],
    ];
  }
  const sum = ls => ls.reduce((s, l) => s + l[2], 0);
  normalize();
  const REF = clone(S); // le modèle présenté = le prix de départ
  BASE = sale(sum(cost(REF)));
  // Le prix suit les coûts : chaque option ajoute ou retire son coût réel, et le total garde toujours le même bénéfice
  function price(st) {
    // pour le client, l'acheminement fait partie du meuble : la livraison reste « offerte »
    const merge = ls => { const o = ls.map(l => l.slice()), s = o.find(l => l[0] === 'struct'), t = o.find(l => l[0] === 'ship'); s[2] += t[2]; t[2] = 0; return o; };
    const ref = merge(cost(REF)), cur = merge(cost(st)), diffs = [];
    cur.forEach((l, i) => {
      if (l[0] === 'ship') return;
      const d = Math.round((l[2] - ref[i][2]) * K / 10) * 10;
      if (d) diffs.push([d < 0 && !l[2] ? 'Retrait : ' + ref[i][1] : l[1], d]);
    });
    const total = sale(sum(cur)), gap = total - BASE - diffs.reduce((s, d) => s + d[1], 0);
    if (gap) { if (diffs.length) diffs[0][1] += gap; else diffs.push(['Ajustement', gap]); }
    return { total, diffs: diffs.filter(d => d[1]) };
  }
  // Détail des coûts pour le vendeur (console) : window.nkCosts()
  window.nkCosts = () => { const c = cost(S), usd = sum(c), p = price(S).total; return { lignesUSD: c.filter(l => l[2]).map(l => [l[1], Math.round(l[2])]), coutUSD: Math.round(usd), coutEUR: Math.round(usd * USD_EUR), prix: p, urssaf: Math.round(p * 0.124), shopify: Math.round(p * 0.02), pub: PUB, net: Math.round(p * NET - usd * USD_EUR - PUB) }; };


  // ---------- Interface ----------
  injectCss();
  root.innerHTML = `
  <div class="nkx">
    <div class="nkx-view">
      <div class="nkx-canvas"></div>
      <div class="nkx-tools">
        <button type="button" data-act="undo" title="Annuler">↶</button><button type="button" data-act="redo" title="Rétablir">↷</button>
        <button type="button" data-act="open">Ouvrir</button><button type="button" data-act="cotes">Cotes</button>
        <button type="button" data-act="front">Vue de face</button><button type="button" data-act="fit">Vue 3D</button>
        <button type="button" data-act="reset">${KREF ? 'Repartir de zéro' : 'Modèle de base'}</button>
        <button type="button" data-act="plan">Voir le plan</button><button type="button" data-act="save">Enregistrer</button>
        <button type="button" data-act="warn" class="nkx-warnbtn" hidden></button>
      </div>
      <div class="nkx-warns" hidden></div>
      <div class="nkx-toast" hidden></div>
      <p class="nkx-hint">${window.matchMedia('(pointer:coarse)').matches ? 'Glissez de côté pour tourner · touchez une colonne' : 'Faites tourner le meuble · cliquez sur une colonne pour l’aménager'}</p>
    </div>
    <div class="nkx-panel">
      <div class="nkx-tabs" role="tablist">
        <button type="button" data-tab="1"><b>01</b>Dimensions</button>
        <button type="button" data-tab="2"><b>02</b>Colonnes</button>
        <button type="button" data-tab="3"><b>03</b>Finitions</button>
        <button type="button" data-tab="4"><b>04</b>Prix</button>
      </div>
      <div class="nkx-body"></div>
      <div class="nkx-foot">
        <div><span>Votre prix</span><div class="nkx-prow"><b class="nkx-total">—</b><s class="nkx-old"></s><em class="nkx-badge"></em></div><small class="nkx-ship"></small></div>
        <button type="button" class="nkx-cta" data-act="cart">Ajouter au panier</button>
      </div>
    </div>
  </div>`;
  const body = root.querySelector('.nkx-body');
  let tab = 1;

  // curseur glissé au doigt : le meuble et le prix bougent en direct
  // une cote se règle au curseur ou se tape au clavier, en cm (comme sur le mètre ruban)
  const range = (label, key, help = '') => `<div class="nkx-r"><div class="nkx-r-top"><span>${label}</span>${key === 'n'
    ? `<b data-out="${key}"></b>`
    : `<label class="nkx-num"><input type="text" inputmode="decimal" autocomplete="off" data-n="${key}" aria-label="${label}, en centimètres"><span>cm</span></label>`}</div><input type="range" data-r="${key}" step="${key === 'n' ? 1 : 10}" aria-label="${label}">${help ? `<small>${help}</small>` : ''}</div>`;
  function bounds(key) {
    const n = S.cols.length;
    return {
      L: LIM.L, F: [0, S.L - 300], H: LIM.H, h: [LIM.h[0], S.H], D: LIM.D,
      n: [1, Math.min(10, Math.floor(S.L / LIM.colW[0]))],
      colw: [LIM.colW[0], Math.min(LIM.colW[1], S.L - (n - 1) * LIM.colW[0])],
    }[key];
  }
  const valOf = key => (key === 'colw' ? S.cols[sel].w : key === 'n' ? S.cols.length : S[key]);
  const outOf = key => (key === 'n' ? pl(S.cols.length, 'colonne') : cmf(valOf(key)) + ' cm');
  function syncRanges() { // remet curseurs et valeurs affichées d'accord avec la configuration
    body.querySelectorAll('[data-r]').forEach(r => {
      const k = r.dataset.r, [a, b] = bounds(k);
      r.min = a; r.max = b; r.value = Math.round(valOf(k));
      r.style.setProperty('--p', ((valOf(k) - a) / Math.max(1, b - a) * 100) + '%');
      const o = body.querySelector(`[data-out="${k}"]`); if (o) o.textContent = outOf(k);
      const nIn = body.querySelector(`[data-n="${k}"]`);
      if (nIn && document.activeElement !== nIn) nIn.value = cmf(valOf(k));
    });
    body.querySelectorAll('.nkx-chips [data-sel] small').forEach((s, i) => S.cols[i] && (s.textContent = Math.round(S.cols[i].w / 10)));
  }
  function applyRange(key, v) {
    if (key === 'colw') return setColWidth(sel, v);
    if (key === 'n') {
      const n = clamp(Math.round(v), ...bounds('n'));
      while (S.cols.length < n) S.cols.push(clone(S.cols[S.cols.length - 1]));
      S.cols.length = n; S.cols.forEach(c => (c.w = S.L / n)); return;
    }
    if (key === 'L') { const f = clamp(v, ...LIM.L) / S.L; S.cols.forEach(c => (c.w *= f)); }
    S[key] = v;
  }
  const btns = (attr, list, cur) => list.map(o => `<button type="button" data-${attr}="${o.id}" class="${cur === o.id ? 'is-on' : ''}">${o.c ? `<i class="nkx-dot" style="background:${o.c}"></i>` : ''}${o.n}</button>`).join('');
  const stepper = (attr, val, label) => `<div class="nkx-step"><button type="button" data-${attr}="-1" aria-label="Moins">−</button><b>${val}</b><button type="button" data-${attr}="1" aria-label="Plus">+</button>${label ? `<span>${label}</span>` : ''}</div>`;
  const check = (attr, on, label, extra = '') => `<label class="nkx-check"><input type="checkbox" data-${attr} ${on ? 'checked' : ''}><span>${label}</span>${extra ? `<em>${extra}</em>` : ''}</label>`;
  const unit = key => { const v = Math.round(diffOf(key) / 10) * 10; return v ? (v > 0 ? '+' : '−') + ' ' + eur(Math.abs(v)) : ''; };
  function diffOf(fnKey) { // prix d'une option, calculé sur la configuration actuelle
    const t = clone(S), k = t.cols[sel];
    if (fnKey === 'led') k.led = !k.led; else if (fnKey === 'rod') k.rod = !k.rod; else if (fnKey === 'counter') k.counter = !k.counter;
    else if (fnKey === 'gshelf') k.glassShelves = !k.glassShelves; else if (fnKey === 'back') t.back = !t.back;
    const d = (sum(cost(t)) - sum(cost(S))) * K;
    return (fnKey === 'led' ? (k.led ? 1 : -1) : fnKey === 'rod' ? (k.rod ? 1 : -1) : fnKey === 'counter' ? (k.counter ? 1 : -1) : fnKey === 'gshelf' ? (k.glassShelves ? 1 : -1) : (t.back ? 1 : -1)) * d;
  }

  function render() {
    root.querySelectorAll('.nkx-tabs button').forEach(b => b.classList.toggle('is-on', +b.dataset.tab === tab));
    const cs = columns(S);
    if (tab === 1) {
      body.innerHTML = `
      <h3>${SHAPE === 'droit' ? 'Les dimensions de votre meuble' : 'L’espace sous votre escalier'}</h3>
      <p class="nkx-help">Glissez les curseurs : le meuble${SHAPE === 'droit' ? '' : ' suit la pente et'} se redessine en direct, le prix s’ajuste tout seul.</p>
      ${SHAPE === 'droit' ? '' : schema()}
      <button type="button" class="nkx-link nkx-guide" data-act="guide">Comment mesurer mon escalier ?</button>
      <p class="nkx-away">Pas chez vous pour mesurer ? <button type="button" class="nkx-link" data-act="mail">Recevoir mon projet par e-mail</button></p>
      ${range('A · Longueur totale', 'L')}
      ${SHAPE === 'droit' ? '' : range('B · Partie plate en haut', 'F', '0 si l’escalier part du mur')}
      ${range(SHAPE === 'droit' ? 'Hauteur' : 'C · Hauteur côté haut', 'H')}
      ${SHAPE === 'droit' ? '' : range('D · Hauteur côté bas', 'h')}
      ${range('Profondeur (sans les portes)', 'D')}
      ${range('Nombre de colonnes', 'n', 'Largeur de chaque colonne réglable dans « Colonnes »')}
      ${SHAPE === 'droit' ? '' : `<p class="nkx-sub">Sens de l’escalier</p><div class="nkx-seg">${btns('dir', [{ id: 'droite', n: 'Monte vers la gauche' }, { id: 'gauche', n: 'Monte vers la droite' }], S.dir)}</div>`}
      <button type="button" class="nkx-next" data-tab="2">Aménager les colonnes →</button>`;
    }
    if (tab === 2) {
      const c = cs[sel], k = c.k, dm = maxDrawers(S, c);
      body.innerHTML = `
      <h3>Colonne ${sel + 1} sur ${cs.length}</h3>
      <div class="nkx-chips">${cs.map(x => `<button type="button" data-sel="${x.i}" class="${x.i === sel ? 'is-on' : ''}">${x.i + 1}<small>${Math.round(x.w / 10)}</small></button>`).join('')}
        <button type="button" data-act="addcol" title="Ajouter une colonne" ${cs.length >= 10 || S.L / (cs.length + 1) < LIM.colW[0] ? 'disabled' : ''}>+</button></div>
      ${cs.length > 1 ? range('Largeur de cette colonne', 'colw', 'Les autres colonnes s’ajustent') : ''}
      <p class="nkx-help">Hauteur utile : ${Math.round(c.hmin / 10)} à ${Math.round(c.hmax / 10)} cm</p>
      <p class="nkx-sub">${k.counter ? 'Façade sous le plan de travail' : 'Façade'}</p>
      <div class="nkx-opts">${FACADES.map(f => `<button type="button" data-fac="${f.id}" class="${k.facade === f.id ? 'is-on' : ''}" ${f.id === 'porte' && c.w > 900 ? 'disabled title="Trop large pour une porte : double porte"' : ''}>${icon(f.id)}<span>${f.n}</span></button>`).join('')}</div>
      ${k.facade === 'porte' || (k.facade === 'verre' && c.w <= 600) ? `<p class="nkx-sub">Ouverture</p><div class="nkx-seg">${btns('hinge', [{ id: 'g', n: 'Charnières à gauche' }, { id: 'd', n: 'Charnières à droite' }], k.hinge)}</div>` : ''}
      ${k.facade === 'coulissant' ? '' : `<p class="nkx-sub">Tiroirs en bas</p>${stepper('dr', k.drawers, dm ? 'max. ' + dm + ' dans cette colonne' : 'colonne trop basse')}`}
      <p class="nkx-sub">Intérieur</p>
      ${stepper('sh', k.shelves, (k.counter || k.facade === 'ouvert') && k.div ? 'étagères par casier' : 'étagères')}
      ${k.counter || k.facade === 'ouvert' ? stepper('dv', k.div || 0, 'séparations verticales dans la niche') : ''}
      ${check('gshelf', k.glassShelves, 'Étagères en verre', unit('gshelf'))}
      ${check('rod', k.rod, 'Penderie (tringle)', unit('rod'))}
      ${c.hmin >= COUNTER_MIN ? check('counter', k.counter, 'Plan de travail à 90 cm', unit('counter')) : ''}
      ${k.counter ? `<p class="nkx-sub">Au-dessus du plan de travail</p><div class="nkx-seg nkx-seg--wrap">${btns('up', [{ id: 'ouvert', n: 'Niche ouverte' }, { id: 'porte', n: 'Niche + placard haut' }, { id: 'verre', n: 'Niche + vitrine haute' }], k.upper)}</div>` : ''}
      ${check('led', k.led, 'Éclairage LED intégré', unit('led'))}
      <div class="nkx-row"><button type="button" class="nkx-link" data-act="all">Copier sur toutes les colonnes</button>
      <button type="button" class="nkx-link" data-act="delcol" ${cs.length < 2 ? 'disabled' : ''}>Supprimer cette colonne</button></div>`;
    }
    if (tab === 3) {
      const anyCounter = S.cols.some(k => k.counter), anyGlass = S.cols.some(k => k.facade === 'verre' || (k.counter && k.upper === 'verre'));
      body.innerHTML = `
      <h3>Finitions</h3>
      <p class="nkx-help">Les 21 teintes de notre nuancier, toutes au même prix. Choisissez la façade, puis appliquez-la partout en un clic si vous voulez un meuble d’une seule couleur.</p>
      <button type="button" class="nkx-link" data-act="samecolor">Même teinte que les façades partout</button>
      <p class="nkx-sub">Structure</p><div class="nkx-sw">${COLORS.map(c => sw(c, 'body')).join('')}</div>
      <p class="nkx-sub">Façades (portes, tiroirs)</p><div class="nkx-sw">${COLORS.map(c => sw(c, 'front')).join('')}</div>
      <p class="nkx-sub">Style des façades</p><div class="nkx-seg nkx-seg--wrap">${btns('sty', STYLES, S.style)}</div>
      <p class="nkx-sub">Fond des niches</p><div class="nkx-sw">${COLORS.map(c => sw(c, 'inside')).join('')}</div>
      <p class="nkx-sub">Étagères</p><div class="nkx-sw">${COLORS.map(c => sw(c, 'shelfc')).join('')}</div>
      ${anyCounter ? `<p class="nkx-sub">Plan de travail</p><div class="nkx-seg nkx-seg--wrap">${btns('top', TOPS, S.top)}</div>
      ${check('merge', S.merge, 'Une seule grande niche au-dessus du plan (sans séparations)')}` : ''}
      ${anyGlass ? `<p class="nkx-sub">Cadre des portes vitrées</p><div class="nkx-seg nkx-seg--wrap">${btns('frm', FRAMES, S.frame)}</div>` : ''}
      <p class="nkx-sub">Poignées</p><div class="nkx-seg nkx-seg--wrap">${btns('hd', HANDLES, S.handle)}</div>
      <p class="nkx-sub">Socle</p><div class="nkx-seg nkx-seg--wrap">${btns('so', SOCLES, S.socle)}</div>
      ${S.socle === 'plinthe' ? check('ledsocle', S.ledSocle, 'Éclairage LED sous le socle', '+ ' + eur(Math.round(SUP.led * K / 10) * 10)) : ''}
      ${check('back', S.back, 'Fond de meuble (panneau arrière)', unit('back'))}`;
    }
    if (tab === 4) {
      const p = price(S);
      body.innerHTML = `
      <h3>Votre ${NAME}, au détail</h3>
      <ul class="nkx-lines"><li><span>${KREF ? 'Meuble de départ (4 colonnes d’étagères)' : 'Prix du modèle présenté · ' + NAME}</span><b>${eur(BASE)}</b></li>
      ${p.diffs.map(d => `<li><span>${d[0]}</span><b>${d[1] >= 0 ? '+' : '−'} ${eur(Math.abs(d[1]))}</b></li>`).join('')}
      ${DISC ? `<li class="nkx-subt"><span>Prix de votre meuble</span><b>${eur(p.total)}</b></li>
      <li class="nkx-disc"><span>Remise −${DISC} %, appliquée au panier</span><b>− ${eur(p.total - afterDisc(p.total))}</b></li>` : ''}
      <li><span>Livraison en France</span><b>Offerte</b></li>
      <li class="nkx-tot"><span>Total TTC à payer</span><b>${eur(afterDisc(p.total))}</b></li></ul>
      <p class="nkx-help"><strong>Délai :</strong> ${DELAI} entre votre commande et la livraison du colis chez vous.</p>
      <p class="nkx-sub">Votre configuration</p>
      <ul class="nkx-conf">${summaryLines().map(l => `<li>${l}</li>`).join('')}</ul>
      <p class="nkx-help">Le prix affiché est le prix final. Après votre commande, nous vous envoyons le plan coté par e-mail : vous vérifiez vos mesures, vous validez, et seulement ensuite nous lançons la fabrication. <button type="button" class="nkx-link" data-act="plan">Voir le plan de mon meuble</button></p>
      <button type="button" class="nkx-next" data-act="cart">Commander mon meuble</button>
      <p class="nkx-help" style="margin-top:14px">Une question avant de commander ? <button type="button" class="nkx-link" data-act="devis">Envoyer ce projet pour un devis gratuit</button></p>
      <p class="nkx-help" style="margin-top:8px">Besoin d’y réfléchir ? <button type="button" class="nkx-link" data-act="mail">Recevoir mon projet par e-mail</button></p>`;
    }
    root.querySelector('[data-act="undo"]').disabled = !past.length;
    root.querySelector('[data-act="redo"]').disabled = !future.length;
    root.querySelector('[data-act="cotes"]').classList.toggle('is-on', showCotes());
    syncRanges();
    update();
  }
  const showCotes = () => (cotes === null ? tab === 1 : cotes);
  function schema() {
    return `<svg class="nkx-schema" viewBox="0 0 320 130" aria-hidden="true">
      ${S.dir === 'droite'
        ? '<path d="M20 112V22H95L300 92V112Z" fill="#EDE6DB" stroke="#16130F"/><text x="160" y="126">A</text><text x="55" y="16">B</text><text x="8" y="70">C</text><text x="306" y="104">D</text>'
        : '<path d="M300 112V22H225L20 92V112Z" fill="#EDE6DB" stroke="#16130F"/><text x="160" y="126">A</text><text x="258" y="16">B</text><text x="306" y="70">C</text><text x="6" y="104">D</text>'}
    </svg>`;
  }
  function sw(c, key) {
    return `<button type="button" class="${S[key] === c.id ? 'is-on' : ''}" data-col="${key}:${c.id}" title="${c.n}"><i style="background:${c.c}"></i><span>${c.n}${c.t ? `<em>${TIERS[c.t]}</em>` : ''}</span></button>`;
  }
  function icon(id) {
    const p = {
      ouvert: '<path d="M6 4h28v32H6z M6 14h28 M6 24h28"/>',
      porte: '<path d="M6 4h28v32H6z"/><circle cx="29" cy="20" r="1.6"/>',
      double: '<path d="M6 4h28v32H6z M20 4v32"/><circle cx="17" cy="20" r="1.4"/><circle cx="23" cy="20" r="1.4"/>',
      verre: '<path d="M6 4h28v32H6z"/><path d="M11 9l8 8M11 15l5 5" opacity=".6"/>',
      coulissant: '<path d="M6 4h22v32H6z"/><path d="M28 10l6 3v20l-6 3" opacity=".6"/>',
    }[id];
    return `<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.4">${p}</svg>`;
  }

  // ---------- Évènements ----------
  root.addEventListener('click', e => {
    const b = e.target.closest('button'); if (!b || b.disabled) return;
    const d = b.dataset;
    if (d.tab) { tab = +d.tab; render(); if (window.innerWidth < 960) root.querySelector('.nkx-panel').scrollIntoView({ behavior: 'smooth' }); return; }
    if (d.sel) { sel = +d.sel; render(); return; }
    const k = S.cols[sel];
    if (d.dir) return commit(() => (S.dir = d.dir));
    if (d.fac) return commit(() => { k.facade = d.fac; });
    if (d.hinge) return commit(() => (k.hinge = d.hinge));
    if (d.up) return commit(() => (k.upper = d.up));
    if (d.dr) return commit(() => (k.drawers = Math.max(0, k.drawers + +d.dr)));
    if (d.sh) return commit(() => (k.shelves += +d.sh));
    if (d.dv) return commit(() => (k.div = (k.div || 0) + +d.dv));
    if (d.col) { const [key, id] = d.col.split(':'); return commit(() => (S[key] = id)); }
    if (d.hd) return commit(() => (S.handle = d.hd));
    if (d.so) return commit(() => (S.socle = d.so));
    if (d.sty) return commit(() => (S.style = d.sty));
    if (d.top) return commit(() => (S.top = d.top));
    if (d.frm) return commit(() => (S.frame = d.frm));
    switch (d.act) {
      case 'all': return commit(() => { S.cols = S.cols.map(c => ({ ...clone(k), w: c.w })); });
      case 'addcol': return commit(() => { const half = k.w / 2; k.w = half; S.cols.splice(sel + 1, 0, { ...clone(k), w: half }); sel++; });
      case 'delcol': return commit(() => { const w = k.w; S.cols.splice(sel, 1); S.cols[Math.max(0, sel - 1)].w += w; sel = Math.max(0, sel - 1); });
      case 'undo': if (past.length) { future.push(clone(S)); S = past.pop(); normalize(); render(); } return;
      case 'redo': if (future.length) { past.push(clone(S)); S = future.pop(); normalize(); render(); } return;
      case 'reset': return commit(() => { S = baseState(); sel = 0; });
      case 'open': open = !open; b.textContent = open ? 'Fermer' : 'Ouvrir'; build(); return;
      case 'cotes': cotes = !showCotes(); b.classList.toggle('is-on', cotes); build(); return;
      case 'fit': userMoved = false; frontView = false; fit(); return;
      case 'front': userMoved = false; frontView = true; fit(); return;
      case 'warn': { const w = root.querySelector('.nkx-warns'); w.hidden = !w.hidden; return; }
      case 'save': saveProject(); return;
      case 'restore': restoreProject(); return;
      case 'guide': openGuide(); return;
      case 'plan': openPlan(); return;
      case 'samecolor': return commit(() => { S.body = S.inside = S.shelfc = S.front; });
      case 'devis': openDevis(); return;
      case 'mail': openMail(); return;
      case 'cart': if (DEMO) { openDemoCart(); return; } addToCart(b); return;
    }
  });

  // ---------- Points d'attention : ce qui mérite une vérification avant de commander ----------
  function warnings(st) {
    const out = [];
    columns(st).forEach(c => {
      const k = c.k, n = c.i + 1;
      if (c.w < 300 && k.facade !== 'ouvert') out.push(`Colonne ${n} : ${cmf(c.w)} cm de large, c’est étroit pour une porte. Une niche ouverte est plus pratique.`);
      if (k.facade === 'porte' && c.w > 750) out.push(`Colonne ${n} : une porte de ${cmf(c.w)} cm est lourde à ouvrir. La double porte est plus confortable.`);
      if (c.hmin < 500 && (k.facade !== 'ouvert' || k.drawers)) out.push(`Colonne ${n} : elle ne fait que ${cmf(c.hmin)} cm de haut côté bas. Vérifiez qu’une porte ou un tiroir y reste pratique.`);
      if (k.rod && st.D < 550) out.push(`Colonne ${n} : une penderie demande au moins 55 cm de profondeur pour des cintres (vous avez ${cmf(st.D)} cm).`);
      if (k.rod && c.hmin < 1000) out.push(`Colonne ${n} : moins d’1 m de haut, une penderie n’y sera pas utile.`);
    });
    if (st.D < 350) out.push(`Profondeur de ${cmf(st.D)} cm : tiroirs et penderie seront peu profonds.`);
    if (!st.back) out.push('Sans fond, le meuble doit être fixé au mur.');
    if (st.L > 4000) out.push('Plus de 4 m de long : le meuble est livré en plusieurs caissons à assembler côte à côte.');
    return out;
  }
  let baseKey = null; // le modèle de départ, avant que le client touche à quoi que ce soit
  function showWarnings() {
    // Pas d'alerte sur le modèle de départ : ces cotes ne sont pas encore celles du client
    const list = JSON.stringify(S) === baseKey ? [] : warnings(S), btn = root.querySelector('.nkx-warnbtn'), box = root.querySelector('.nkx-warns');
    btn.hidden = !list.length; btn.textContent = '⚠ ' + list.length + (list.length > 1 ? ' points à vérifier' : ' point à vérifier');
    box.innerHTML = `<b>À vérifier avant de commander</b><ul>${list.map(x => `<li>${x}</li>`).join('')}</ul><button type="button" class="nkx-link" data-act="warn">Fermer</button>`;
    if (!list.length) box.hidden = true;
  }

  // ---------- Enregistrer le projet (dans ce navigateur) ----------
  const SAVE_KEY = 'millimetre-projet-' + (HANDLE || 'meuble');
  function toast(msg) {
    const t = root.querySelector('.nkx-toast'); t.textContent = msg; t.hidden = false;
    clearTimeout(toast.tm); toast.tm = setTimeout(() => (t.hidden = true), 3200);
  }
  function saveProject() {
    try { localStorage.setItem(SAVE_KEY, JSON.stringify({ at: Date.now(), S })); toast('Projet enregistré sur cet appareil. Vous le retrouverez en revenant sur cette page.'); }
    catch (e) { toast('Enregistrement impossible dans ce navigateur. Utilisez le devis gratuit pour nous l’envoyer.'); }
  }
  function savedProject() { try { return JSON.parse(localStorage.getItem(SAVE_KEY) || 'null'); } catch (e) { return null; } }
  function restoreProject() {
    const p = savedProject(); if (!p) return;
    commit(() => { S = p.S; sel = 0; });
    root.querySelector('.nkx-resume')?.remove(); toast('Projet enregistré rechargé.');
  }

  // ---------- Fenêtres : guide de mesure, devis, récapitulatif ----------
  function modal(html) {
    const m = document.createElement('div'); m.className = 'nkx-modal';
    m.innerHTML = `<div class="nkx-modal__box" role="dialog" aria-modal="true"><button type="button" class="nkx-modal__x" aria-label="Fermer">×</button>${html}</div>`;
    m.addEventListener('click', e => { if (e.target === m || e.target.closest('.nkx-modal__x') || e.target.closest('[data-close]')) m.remove(); });
    document.body.appendChild(m); return m;
  }
  function openGuide() {
    modal(`<p class="nkx-k">Guide de mesure</p><h3>Mesurer sous votre escalier en 5 cotes</h3>
      <svg class="nkx-guide-svg" viewBox="0 0 520 250" aria-hidden="true">
        <path d="M40 210V40H150L480 170V210Z" fill="#EDE6DB" stroke="#16130F" stroke-width="1.5"/>
        <path d="M150 40L480 170" stroke="#6E5236" stroke-width="5" stroke-dasharray="2 9" opacity=".5"/>
        <g stroke="#B39360" stroke-width="1.4" fill="none"><path d="M40 232H480M40 226v12M480 226v12"/><path d="M40 22H150M40 16v12M150 16v12"/><path d="M18 40V210M12 40h12M12 210h12"/><path d="M500 170V210M494 170h12M494 210h12"/></g>
        <g font-family="Inter,sans-serif" font-size="15" font-weight="600" fill="#6E5236"><text x="252" y="248">A</text><text x="88" y="16">B</text><text x="2" y="130">C</text><text x="508" y="196">D</text></g>
      </svg>
      <ol class="nkx-guide-list">
        <li><b>A · Longueur au sol</b> du mur jusqu’au bout de l’escalier, au ras du sol.</li>
        <li><b>B · Partie plate en haut</b> : la longueur sous le palier, avant que la pente commence (0 si la pente part du mur).</li>
        <li><b>C · Hauteur côté haut</b>, du sol jusque sous la marche la plus haute.</li>
        <li><b>D · Hauteur côté bas</b>, du sol jusque sous la dernière marche, là où le meuble s’arrête.</li>
        <li><b>Profondeur</b>, du devant de l’escalier jusqu’au mur du fond.</li>
      </ol>
      <p class="nkx-help">Mesurez chaque cote deux fois, au mètre ruban rigide. Si votre mur ou votre sol n’est pas droit, notez la plus petite valeur. Un doute ? Envoyez-nous une photo avec le devis gratuit, on vérifie avec vous.</p>
      <button type="button" class="nkx-next" data-close>J’ai compris</button>`);
  }
  // ---------- Plan coté (vue de face), celui envoyé au client pour validation avant fabrication ----------
  // Référence courte, identique dans la commande et sur le plan
  function planRef(st) {
    const s = JSON.stringify(st); let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return 'MM-' + (h >>> 0).toString(36).toUpperCase().slice(-6).padStart(6, '0');
  }
  function planSVG(st) {
    const L = st.L, H = Math.max(st.H, st.h), fs = Math.max(L, H) / 42; // taille du texte, en mm du dessin
    const mL = fs * 7, mR = fs * 7, mT = fs * 5, mB = fs * 9;
    const W = L + mL + mR, Ht = H + mT + mB;
    const X = x => mL + x, Y = y => mT + (H - y);
    const ink = '#16130F', wood = '#6E5236', mute = '#9D9488';
    const pts = SHAPE === 'droit' ? [[0, 0], [L, 0], [L, st.H], [0, st.H]]
      : st.dir === 'droite' ? [[0, 0], [L, 0], [L, st.h], [st.F, st.H], [0, st.H]]
      : [[0, 0], [L, 0], [L, st.H], [L - st.F, st.H], [0, st.h]];
    const poly = pts.map(p => X(p[0]).toFixed(1) + ',' + Y(p[1]).toFixed(1)).join(' ');
    let g = '';
    const line = (x1, y1, x2, y2, w = fs / 9, c = ink, dash = '') => (g += `<line x1="${X(x1).toFixed(1)}" y1="${Y(y1).toFixed(1)}" x2="${X(x2).toFixed(1)}" y2="${Y(y2).toFixed(1)}" stroke="${c}" stroke-width="${w.toFixed(1)}"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`);
    const rect = (x0, y0, x1, y1, fill = 'none', w = fs / 10) => (g += `<rect x="${X(x0).toFixed(1)}" y="${Y(y1).toFixed(1)}" width="${(x1 - x0).toFixed(1)}" height="${(y1 - y0).toFixed(1)}" fill="${fill}" stroke="${ink}" stroke-width="${w.toFixed(1)}"/>`);
    const text = (x, y, t, o = {}) => (g += `<text x="${(o.raw ? x : X(x)).toFixed(1)}" y="${(o.raw ? y : Y(y)).toFixed(1)}" font-size="${(o.s || fs).toFixed(1)}" fill="${o.c || wood}" text-anchor="${o.a || 'middle'}" font-family="Inter, Arial, sans-serif" font-weight="${o.w || 600}"${o.rot ? ` transform="rotate(-90 ${(o.raw ? x : X(x)).toFixed(1)} ${(o.raw ? y : Y(y)).toFixed(1)})"` : ''}>${t}</text>`);
    const base = baseH(st);
    if (base) line(0, base, L, base, fs / 12, mute);
    columns(st).forEach(c => {
      const k = c.k, lay = layout(st, c), top = Math.max(c.h0, c.h1) + 50, gap = 12;
      if (c.i) line(c.x0, 0, c.x0, hAt(st, c.x0), fs / 8);
      const x0 = c.x0 + gap, x1 = c.x1 - gap;
      // tiroirs
      for (let d = 0; d < k.drawers; d++) {
        const y0 = lay.yb + d * lay.dh;
        rect(x0, y0 + 4, x1, y0 + lay.dh - 4, '#F3EEE6');
        line((x0 + x1) / 2 - Math.min(80, c.w / 6), y0 + lay.dh / 2, (x0 + x1) / 2 + Math.min(80, c.w / 6), y0 + lay.dh / 2, fs / 7);
      }
      // façade du bas (ou de toute la hauteur)
      const yLow0 = k.drawers ? lay.yD : lay.yb, yLow1 = lay.counterY !== null ? lay.counterY : top;
      const facade = (type, a, b, hinge) => {
        if (type === 'ouvert') return;
        if (type === 'coulissant') { rect(x0, a, x1, b, '#F7F2EA'); text((x0 + x1) / 2, (a + Math.min(b, c.hmin)) / 2, '⇄', { s: fs * 1.4, c: ink }); return; }
        const n = leaves(type, c.w), wl = (x1 - x0) / n;
        for (let i = 0; i < n; i++) {
          const a0 = x0 + i * wl, a1 = a0 + wl;
          rect(a0 + 3, a, a1 - 3, b, type === 'verre' ? 'rgba(160,190,200,.18)' : '#FBF8F3');
          // ouverture : pointillés vers les charnières, comme sur un plan de menuisier
          const left = n === 2 ? i === 0 : hinge === 'g', ym = (a + Math.min(b, hAt(st, (a0 + a1) / 2))) / 2;
          const xh = left ? a0 + 3 : a1 - 3, xo = left ? a1 - 3 : a0 + 3;
          line(xo, a + 10, xh, ym, fs / 14, mute, `${fs / 3} ${fs / 4}`);
          line(xo, Math.min(b, hAt(st, xo)) - 10, xh, ym, fs / 14, mute, `${fs / 3} ${fs / 4}`);
        }
      };
      facade(k.facade, yLow0, yLow1, k.hinge);
      // étagères visibles dans les parties ouvertes
      const shelves = (a, b) => { for (let s = 1; s <= k.shelves; s++) { const y = a + (b - a) * s / (k.shelves + 1); line(c.x0 + 6, y, c.x1 - 6, y, fs / 10, wood, `${fs / 2.5} ${fs / 4}`); } };
      if (k.facade === 'ouvert') shelves(yLow0, Math.min(yLow1, c.hmin - 20));
      if (lay.counterY !== null) {
        rect(c.x0, lay.counterY, c.x1, lay.counterY + 30, '#D9CDBB', fs / 12);
        if (lay.upper) facade(lay.upper.type, lay.upper.y0, top, k.hinge);
        else shelves(lay.counterY + 30, c.hmin - 20);
      }
      if (k.rod) line(c.x0 + 40, Math.min(c.hmin, yLow1) - 120, c.x1 - 40, Math.min(c.hmin, yLow1) - 120, fs / 5, ink);
      if (k.led) text((c.x0 + c.x1) / 2, Math.min(c.hmin, top) - fs * 1.6, 'LED', { s: fs * 0.7, c: '#B39360' });
      // numéro de colonne
      g += `<circle cx="${X((c.x0 + c.x1) / 2).toFixed(1)}" cy="${Y(base + fs * 1.4).toFixed(1)}" r="${(fs * 0.85).toFixed(1)}" fill="${ink}"/>`;
      text((c.x0 + c.x1) / 2, base + fs * 1.1, c.i + 1, { s: fs * 0.9, c: '#fff' });
    });
    // l'intérieur est découpé par le contour : portes et étagères suivent la pente
    const inner = g; g = '';
    columns(st).forEach(c => { // largeur de chaque colonne, sous le meuble
      line(c.x0, -fs * 2.2, c.x1, -fs * 2.2, fs / 14, wood); line(c.x0, -fs * 1.8, c.x0, -fs * 2.6, fs / 14, wood);
      text((c.x0 + c.x1) / 2, -fs * 3.4, cmf(c.w), { s: fs * 0.8 });
    });
    line(L, -fs * 1.8, L, -fs * 2.6, fs / 14, wood);
    g = `<defs><clipPath id="nkclip"><polygon points="${poly}"/></clipPath></defs><polygon points="${poly}" fill="#FFFFFF" stroke="none"/><g clip-path="url(#nkclip)">${inner}</g>`
      + g + `<polygon points="${poly}" fill="none" stroke="${ink}" stroke-width="${(fs / 5).toFixed(1)}" stroke-linejoin="round"/>`;
    // cotes générales
    line(0, -fs * 5, L, -fs * 5, fs / 10, wood); line(0, -fs * 4.4, 0, -fs * 5.6, fs / 10, wood); line(L, -fs * 4.4, L, -fs * 5.6, fs / 10, wood);
    text(L / 2, -fs * 6.3, 'Longueur ' + cmf(L) + ' cm', { s: fs * 1.05, c: ink });
    const hiX = SHAPE === 'droit' || st.dir === 'droite' ? -fs * 2.5 : L + fs * 2.5, loX = SHAPE === 'droit' || st.dir === 'droite' ? L + fs * 2.5 : -fs * 2.5;
    line(hiX, 0, hiX, st.H, fs / 10, wood); text(hiX + (hiX < 0 ? -fs * 0.9 : fs * 1.9), st.H / 2, 'H ' + cmf(st.H) + ' cm', { s: fs * 0.95, c: ink, rot: true });
    if (SHAPE !== 'droit') {
      line(loX, 0, loX, st.h, fs / 10, wood); text(loX + (loX < 0 ? -fs * 0.9 : fs * 1.9), st.h / 2, cmf(st.h) + ' cm', { s: fs * 0.95, c: ink, rot: true });
      if (st.F > 0) { const fx0 = st.dir === 'droite' ? 0 : L - st.F; line(fx0, st.H + fs * 1.6, fx0 + st.F, st.H + fs * 1.6, fs / 10, wood); text(fx0 + st.F / 2, st.H + fs * 2.2, 'plat ' + cmf(st.F) + ' cm', { s: fs * 0.8 }); }
    }
    text(L / 2, -fs * 7.8, 'Profondeur ' + cmf(st.D) + ' cm · vue de face · cotes en cm', { s: fs * 0.75, c: mute, w: 500 });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W.toFixed(0)} ${Ht.toFixed(0)}" class="nkx-plan-svg" role="img" aria-label="Plan coté du meuble, vue de face">${g}</svg>`;
  }
  function openPlan() {
    const ref = planRef(S), date = new Date().toLocaleDateString('fr-FR');
    const m = modal(`<p class="nkx-k">Plan de principe · ${ref} · ${date}</p>
      <h3>Le plan de votre meuble${KREF ? '' : ' · ' + NAME}</h3>
      <div class="nkx-plan">${planSVG(S)}</div>
      <ul class="nkx-conf">${summaryLines().map(l => `<li>${l}</li>`).join('')}</ul>
      <p class="nkx-demo"><strong>Rien n’est fabriqué sans votre accord.</strong> Après votre commande, nous vous envoyons ce plan par e-mail. Vous vérifiez vos cotes (on peut les vérifier ensemble sur photo), puis vous validez : la fabrication ne démarre qu’à ce moment-là.</p>
      <div class="nkx-row"><button type="button" class="nkx-next" data-close>Retour au configurateur</button><button type="button" class="nkx-link" data-dl>Télécharger le plan (image)</button></div>`);
    m.querySelector('.nkx-modal__box').classList.add('nkx-modal__box--wide');
    m.querySelector('[data-dl]').addEventListener('click', () => downloadPlan(ref, m));
  }
  function downloadPlan(ref, m) {
    const svg = planSVG(S), vb = svg.match(/viewBox="0 0 (\d+) (\d+)"/), w = 2000, h = Math.round(w * vb[2] / vb[1]);
    const img = new Image();
    img.onload = () => {
      const cv = document.createElement('canvas'); cv.width = w; cv.height = h; const g = cv.getContext('2d');
      g.fillStyle = '#fff'; g.fillRect(0, 0, w, h); g.drawImage(img, 0, 0, w, h);
      const url = cv.toDataURL('image/png');
      const a = document.createElement('a'); a.download = 'plan-' + ref + '.png'; a.href = url; document.body.appendChild(a); a.click(); a.remove();
      // le plan devient aussi une image : clic droit ou appui long pour l'enregistrer si le téléchargement est bloqué
      const box = m && m.querySelector('.nkx-plan');
      if (box) box.innerHTML = `<img src="${url}" alt="Plan ${ref}" class="nkx-plan-svg"><p class="nkx-help" style="text-align:center">Si le fichier ne s’est pas téléchargé : clic droit (ou appui long) sur l’image, puis « Enregistrer l’image ».</p>`;
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }
  // Outil interne : recharge la configuration d'une commande (propriété _config) pour en sortir le plan
  window.nkLoadConfig = obj => {
    const o = typeof obj === 'string' ? JSON.parse(obj) : obj; delete o.shape;
    commit(() => { S = o; sel = 0; }); openPlan();
  };
  function projectText() {
    const p = price(S);
    return `Modèle : ${KREF ? 'Créé de zéro' : NAME}\nPrix affiché : ${eur(afterDisc(p.total))}\n` + summaryLines().join('\n');
  }
  function openDevis() {
    if (window.nkOpenDevis) window.nkOpenDevis({ projet: projectText(), lien: shareUrl() });
  }
  // Recevoir le projet par e-mail : pour celui qui n'est pas chez lui pour mesurer
  function openMail() {
    try { localStorage.setItem(SAVE_KEY, JSON.stringify({ at: Date.now(), S })); } catch (e) {}
    if (window.nkOpenDevis) window.nkOpenDevis({ projet: projectText(), lien: shareUrl(), mode: 'mail' });
  }
  // Lien qui rouvre exactement ce meuble, sur n'importe quel appareil
  function shareUrl() {
    const b = btoa(unescape(encodeURIComponent(JSON.stringify(S))));
    return location.origin + location.pathname + '?p=' + encodeURIComponent(b) + '#nk-configurer';
  }
  // Un lien ?p= vient de l'extérieur : on n'accepte que des valeurs simples (nombres, oui/non, identifiants)
  function sharedState(raw) {
    try {
      const o = JSON.parse(decodeURIComponent(escape(atob(raw))));
      const ok = v => v === null || typeof v === 'boolean' || (typeof v === 'number' && isFinite(v)) || (typeof v === 'string' && /^[#\w-]{0,40}$/.test(v));
      const flat = x => x && typeof x === 'object' && Object.keys(x).every(k => k === 'cols' || ok(x[k]));
      if (!flat(o) || !Array.isArray(o.cols) || !o.cols.length || o.cols.length > 10 || !o.cols.every(flat)) return null;
      return ['L', 'F', 'H', 'h', 'D'].every(k => typeof o[k] === 'number') ? o : null;
    } catch (e) { return null; }
  }
  function openDemoCart() {
    const p = price(S);
    const m = modal(`<p class="nkx-k">Votre meuble est prêt</p><h3>${KREF ? 'Votre meuble créé de zéro' : 'Votre ' + NAME}</h3>
      <p class="nkx-modal__price">${eur(afterDisc(p.total))} <small>livraison offerte · ${DELAI}</small></p>
      <ul class="nkx-conf">${summaryLines().map(l => `<li>${l}</li>`).join('')}</ul>
      <p class="nkx-demo">Site parallèle de test : sur la vraie boutique, ce bouton ajoute le meuble au panier pour le paiement en ligne.</p>
      <div class="nkx-row"><button type="button" class="nkx-next" data-close>Continuer à modifier</button><button type="button" class="nkx-link" data-close data-act-devis>Envoyer ce projet pour un devis gratuit</button></div>`);
    m.querySelector('[data-act-devis]').addEventListener('click', openDevis);
  }
  // curseurs : mise à jour en direct pendant le glissé, une seule étape d'historique au lâcher
  let dragSnap = null;
  root.addEventListener('input', e => {
    const r = e.target; if (!r.dataset || !r.dataset.r) return;
    if (!dragSnap) dragSnap = clone(S);
    applyRange(r.dataset.r, +r.value); normalize(); syncRanges(); update();
  });
  root.addEventListener('change', e => {
    const t = e.target, d = t.dataset;
    if (d.n) { // cote tapée au clavier, en cm
      const v = parseFloat(String(t.value).replace(/\s/g, '').replace(',', '.'));
      if (isNaN(v)) { render(); return; }
      const [a, b] = bounds(d.n), mmv = clamp(Math.round(v * 10), a, b);
      if (mmv !== Math.round(v * 10)) toast(`Cote ramenée à ${cmf(mmv)} cm (entre ${cmf(a)} et ${cmf(b)} cm)`);
      return commit(() => applyRange(d.n, mmv));
    }
    if (d.r) {
      if (dragSnap) { past.push(dragSnap); if (past.length > 60) past.shift(); future.length = 0; dragSnap = null; }
      render(); return;
    }
    const k = S.cols[sel];
    if ('led' in d) return commit(() => (k.led = t.checked));
    if ('rod' in d) return commit(() => (k.rod = t.checked));
    if ('counter' in d) return commit(() => (k.counter = t.checked));
    if ('gshelf' in d) return commit(() => (k.glassShelves = t.checked));
    if ('back' in d) return commit(() => (S.back = t.checked));
    if ('merge' in d) return commit(() => (S.merge = t.checked));
    if ('ledsocle' in d) return commit(() => (S.ledSocle = t.checked));
  });

  function summaryLines(withRef = false) { // withRef : références fournisseur, pour la commande et le devis seulement
    const st = S;
    const dims = SHAPE === 'droit'
      ? `L ${cmf(st.L)} × H ${cmf(st.H)} × P ${cmf(st.D)} cm`
      : `L ${cmf(st.L)} cm (partie haute ${cmf(st.F)}) × H ${cmf(st.H)} → ${cmf(st.h)} cm × P ${cmf(st.D)} cm, escalier montant vers la ${st.dir === 'droite' ? 'gauche' : 'droite'}`;
    const cols = columns(st).map(c => {
      const k = c.k, f = FACADES.find(x => x.id === k.facade).n;
      const bits = [cmf(c.w) + ' cm', k.facade === 'ouvert' ? 'ouverte' : f + (k.facade === 'porte' ? (k.hinge === 'g' ? ' (gauche)' : ' (droite)') : '')];
      if (k.drawers) bits.push(pl(k.drawers, 'tiroir'));
      bits.push(pl(k.shelves, k.glassShelves ? 'étagère verre' : 'étagère', k.glassShelves ? 'étagères verre' : '') + (k.div && (k.counter || k.facade === 'ouvert') ? ' par casier, ' + (k.div + 1) + ' casiers' : ''));
      if (k.rod) bits.push('penderie');
      if (k.counter) bits.push('plan de travail' + (k.upper === 'porte' ? ' + placard haut' : k.upper === 'verre' ? ' + vitrine haute' : ' + niche'));
      if (k.led) bits.push('LED');
      return `Colonne ${c.i + 1} : ${bits.join(', ')}`;
    });
    // la référence fournisseur suit chaque teinte pour passer la commande sans erreur
    const cn = id => { const c = colorOf(id); return c.n + (withRef && c.ref ? ' [' + c.ref + ']' : ''); };
    const fin = [`Structure ${cn(st.body)}`, `façades ${cn(st.front)} ${styleOf(st.style).n.toLowerCase()}`, `fond ${cn(st.inside)}`, `étagères ${cn(st.shelfc)}`];
    if (st.cols.some(k => k.counter)) fin.push(`plan ${st.top === 'struct' ? 'assorti' : topOf(st.top).n}` + (st.merge ? ', niche d’un seul tenant' : ''));
    if (st.ledSocle && st.socle === 'plinthe') fin.push('LED sous le socle');
    return [dims, ...cols, fin.join(' · '), `Poignées ${handleOf(st.handle).n} · ${socleOf(st.socle).n}${st.back ? ' · avec fond' : ' · sans fond'}`];
  }
  function update() {
    const p = price(S);
    root.querySelector('.nkx-total').textContent = eur(afterDisc(p.total));
    root.querySelector('.nkx-old').textContent = DISC ? eur(p.total) : '';
    root.querySelector('.nkx-badge').textContent = DISC ? '−' + DISC + ' %' : '';
    root.querySelector('.nkx-ship').textContent = 'Livraison offerte · ' + DELAI;
    showWarnings();
    build();
  }

  async function addToCart(btn) {
    const p = price(S), old = btn.textContent;
    btn.disabled = true; btn.textContent = 'Ajout…';
    try {
      // paliers de prix répartis sur plusieurs produits cachés (Shopify n'expose que 250 variantes par produit)
      const handle = p.total < 1490 ? 'nook-sur-mesure-0' : p.total <= 3980 ? 'nook-sur-mesure' : p.total <= 6480 ? 'nook-sur-mesure-2' : p.total <= 8980 ? 'nook-sur-mesure-3' : 'nook-sur-mesure-4';
      const prod = await (await fetch('/products/' + handle + '.js')).json();
      const v = prod.variants.find(x => Math.round(x.price / 100) === p.total);
      if (!v) { btn.textContent = old; btn.disabled = false; tab = 4; render(); alert('Cette configuration dépasse nos dimensions standard : réduisez un peu la taille ou les options pour pouvoir la commander.'); return; }
      const props = { 'Modèle': NAME };
      const sl = summaryLines();
      sl.forEach((l, i) => { const m = l.match(/^(Colonne \d+) : (.*)$/); props[i === 0 ? 'Dimensions' : m ? m[1] : i === sl.length - 2 ? 'Finitions' : 'Équipements'] = m ? m[2] : l; });
      props['Délai'] = 'Livraison offerte, ' + DELAI;
      // références fournisseur : propriété cachée (le « _ » la masque au client, elle reste visible dans la commande)
      const slr = summaryLines(true); props['_Références fournisseur'] = slr[slr.length - 2];
      if (IMAGE) props['_image'] = IMAGE; // photo du modèle de base, affichée dans le panier
      props['_url'] = location.pathname;
      props['Référence du plan'] = planRef(S) + ' (plan envoyé par e-mail pour validation avant fabrication)';
      props['_config'] = JSON.stringify({ shape: SHAPE, ...S });
      const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: props }] }) });
      if (!r.ok) throw new Error('ajout');
      location.href = '/cart';
    } catch (err) {
      btn.textContent = 'Erreur, réessayez'; setTimeout(() => { btn.textContent = old; btn.disabled = false; }, 2500);
    }
  }

  // ---------- Scène 3D ----------
  const host = root.querySelector('.nkx-canvas');
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  const SMALL = window.innerWidth < 900; // téléphone ou tablette : rendu allégé
  renderer.setPixelRatio(Math.min(SMALL ? 1.5 : 2, window.devicePixelRatio));
  renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace; renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.0;
  host.appendChild(renderer.domElement);
  const scene = new THREE.Scene(); scene.background = new THREE.Color('#EEE9E1');
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(renderer), 0.04).texture;
  const camera = new THREE.PerspectiveCamera(32, 1, 0.05, 80);
  const controls = new OrbitControls(camera, renderer.domElement);
  renderer.domElement.style.touchAction = 'pan-y'; // au doigt : haut/bas fait défiler la page, côté fait tourner le meuble
  // Rendu à la demande : on ne redessine que si la vue ou le meuble a changé (sinon le téléphone calcule 60 images/s pour rien)
  let dirty = true; controls.addEventListener('change', () => (dirty = true));
  controls.enableDamping = true; controls.minPolarAngle = 0.35; controls.maxPolarAngle = 1.62; controls.minAzimuthAngle = -1.2; controls.maxAzimuthAngle = 1.2; controls.minDistance = 0.6; controls.maxDistance = 30;
  scene.add(new THREE.HemisphereLight('#ffffff', '#c9bba8', 0.35));
  const sun = new THREE.DirectionalLight('#fff3e2', 1.35); sun.castShadow = true; sun.shadow.mapSize.set(SMALL ? 1024 : 2048, SMALL ? 1024 : 2048); sun.shadow.bias = -0.0003; sun.shadow.normalBias = 0.02; sun.shadow.radius = 4;
  scene.add(sun); scene.add(sun.target);
  const fill = new THREE.DirectionalLight('#e9eef5', 0.35); fill.position.set(6, 3, 8); scene.add(fill);

  // Textures procédurales (aucun fichier à charger)
  const texCache = {};
  const rng = s => () => ((s = (s * 16807) % 2147483647) / 2147483647);
  function canvasTex(key, w, h, draw, repeat) {
    if (texCache[key]) return texCache[key];
    const cv = document.createElement('canvas'); cv.width = w; cv.height = h; draw(cv.getContext('2d'), w, h);
    const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.anisotropy = 8;
    if (repeat) t.repeat.set(repeat, repeat);
    return (texCache[key] = t);
  }
  const woodTex = hex => canvasTex('w' + hex, 512, 1024, (g, w, h) => {
    const r = rng(11); g.fillStyle = hex; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 90; i++) { // fil du bois, vertical
      const x = r() * w, a = r() * 0.16, lw = 0.6 + r() * 2.4;
      g.strokeStyle = `rgba(${r() > 0.5 ? '70,40,18' : '255,240,220'},${a})`; g.lineWidth = lw; g.beginPath(); g.moveTo(x, 0);
      for (let y = 0; y <= h; y += 24) g.lineTo(x + Math.sin(y / (80 + r() * 60) + i) * (3 + r() * 5), y);
      g.stroke();
    }
    for (let i = 0; i < 2500; i++) { g.fillStyle = `rgba(60,35,15,${r() * 0.05})`; g.fillRect(r() * w, r() * h, 1, 3 + r() * 6); }
  });
  const marbleTex = () => canvasTex('marbre', 1024, 1024, (g, w, h) => {
    const r = rng(5); g.fillStyle = '#F2F0EC'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 26; i++) {
      g.strokeStyle = `rgba(120,120,125,${0.05 + r() * 0.22})`; g.lineWidth = 0.5 + r() * 2.5; g.beginPath();
      let x = r() * w, y = 0; g.moveTo(x, y);
      while (y < h) { x += (r() - 0.45) * 60; y += 20 + r() * 40; g.lineTo(x, y); }
      g.stroke();
    }
  });
  const stoneTex = hex => canvasTex('s' + hex, 512, 512, (g, w, h) => {
    const r = rng(3); g.fillStyle = hex; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 9000; i++) { const v = r() > 0.5 ? 255 : 90; g.fillStyle = `rgba(${v},${v},${v},${r() * 0.07})`; g.fillRect(r() * w, r() * h, 1 + r() * 2, 1 + r() * 2); }
  });
  const floorTex = canvasTex('sol', 1024, 1024, (g, w, h) => { // parquet chêne clair, lames de 15 cm
    const r = rng(17), pw = w / 8;
    for (let i = 0; i < 8; i++) for (let y = -r() * 400; y < h; ) {
      const len = 300 + r() * 500, v = 205 + r() * 25;
      g.fillStyle = `rgb(${v},${v * 0.86},${v * 0.68})`; g.fillRect(i * pw, y, pw, len);
      for (let k = 0; k < 14; k++) { g.strokeStyle = `rgba(90,60,30,${r() * 0.1})`; g.lineWidth = 1; const x = i * pw + r() * pw; g.beginPath(); g.moveTo(x, y); g.lineTo(x + (r() - 0.5) * 6, y + len); g.stroke(); }
      g.fillStyle = 'rgba(80,55,30,.35)'; g.fillRect(i * pw, y, pw, 2); y += len;
    }
    g.fillStyle = 'rgba(80,55,30,.3)'; for (let i = 0; i <= 8; i++) g.fillRect(i * pw - 1, 0, 2, h);
  }, 1);
  floorTex.repeat.set(12, 12);
  const glowTex = canvasTex('glow', 64, 256, (g, w, h) => {
    const gr = g.createLinearGradient(0, 0, 0, h); gr.addColorStop(0, 'rgba(255,244,214,0.97)'); gr.addColorStop(0.1, 'rgba(255,214,150,0.85)'); gr.addColorStop(0.45, 'rgba(226,158,92,0.45)'); gr.addColorStop(1, 'rgba(92,58,30,0.42)');
    g.fillStyle = gr; g.fillRect(0, 0, w, h);
  });
  function labelTex(text) {
    const key = 'l:' + text; if (texCache[key]) return texCache[key];
    const cv = document.createElement('canvas'), g = cv.getContext('2d'); g.font = '500 44px Inter, Arial, sans-serif';
    const w = Math.ceil(g.measureText(text).width) + 36; cv.width = w; cv.height = 68;
    g.fillStyle = 'rgba(22,19,15,.88)'; g.beginPath(); g.roundRect(0, 0, w, 68, 34); g.fill();
    g.font = '500 44px Inter, Arial, sans-serif'; g.fillStyle = '#fff'; g.textBaseline = 'middle'; g.fillText(text, 18, 36);
    const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; t.userData = { aspect: w / 68 };
    return (texCache[key] = t);
  }

  // Décor fixe : sol en parquet et mur
  // sol : parquet, ou grand carrelage gris façon pierre (photo Galerie)
  const tileTex = canvasTex('carrelage', 1024, 1024, (g, w, h) => {
    const r = rng(23); g.fillStyle = '#8F8B86'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 4; i++) { const v = 128 + r() * 20; g.fillStyle = `rgba(${v},${v - 3},${v - 6},.55)`; g.fillRect((i % 2) * w / 2, Math.floor(i / 2) * h / 2, w / 2, h / 2); }
    for (let i = 0; i < 18; i++) { g.strokeStyle = `rgba(230,228,224,${0.05 + r() * 0.15})`; g.lineWidth = 0.5 + r() * 1.5; g.beginPath(); let x = r() * w, y = 0; g.moveTo(x, y); while (y < h) { x += (r() - 0.5) * 50; y += 25; g.lineTo(x, y); } g.stroke(); }
    g.fillStyle = 'rgba(60,58,55,.6)'; g.fillRect(0, 0, w, 3); g.fillRect(0, h / 2, w, 3); g.fillRect(0, 0, 3, h); g.fillRect(w / 2, 0, 3, h);
  });
  tileTex.repeat.set(33, 33);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), pre.floor === 'gris'
    ? new THREE.MeshStandardMaterial({ map: tileTex, roughness: 0.22, envMapIntensity: 0.7 })
    : new THREE.MeshStandardMaterial({ map: floorTex, roughness: 0.62, envMapIntensity: 0.4 }));
  floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; scene.add(floor);
  const wall = new THREE.Mesh(new THREE.PlaneGeometry(40, 14), new THREE.MeshStandardMaterial({ color: '#EFEBE4', roughness: 0.95, envMapIntensity: 0.3 }));
  wall.position.set(0, 7, -0.02); wall.receiveShadow = true; scene.add(wall);
  const group = new THREE.Group(); scene.add(group);

  const mats = [];
  const M = m => (mats.push(m), m);
  function finish(id) {
    const c = colorOf(id);
    return M(new THREE.MeshStandardMaterial({ color: c.wood ? '#ffffff' : c.c, map: c.wood ? woodTex(c.c) : null, roughness: c.gloss ? 0.14 : c.wood ? 0.55 : 0.68, envMapIntensity: c.gloss ? 1 : 0.45 }));
  }
  function topMat(st) {
    const t = topOf(st.top);
    if (t.id === 'struct') return finish(st.body);
    if (t.marble) return M(new THREE.MeshStandardMaterial({ map: marbleTex(), roughness: 0.18, envMapIntensity: 0.9 }));
    if (t.stone) return M(new THREE.MeshStandardMaterial({ map: stoneTex(t.c), roughness: 0.5 }));
    return M(new THREE.MeshStandardMaterial({ color: t.c, roughness: 0.45, envMapIntensity: 0.6 }));
  }
  const mm = v => v / 1000;
  function box(w, h, d, m, x, y, z, col) {
    const b = new THREE.Mesh(new THREE.BoxGeometry(mm(w), mm(h), mm(d)), m);
    b.position.set(mm(x + w / 2), mm(y + h / 2), mm(z + d / 2)); b.castShadow = b.receiveShadow = true; b.userData.col = col; group.add(b); return b;
  }
  // panneau vu de face, dessus incliné (suit la pente) ; bev = petit chanfrein des façades
  function slab(x0, x1, yb, ht0, ht1, depth, m, z, col, bev = 0) {
    const s = new THREE.Shape();
    s.moveTo(mm(x0 + bev), mm(yb + bev)); s.lineTo(mm(x1 - bev), mm(yb + bev)); s.lineTo(mm(x1 - bev), mm(ht1 - bev)); s.lineTo(mm(x0 + bev), mm(ht0 - bev)); s.closePath();
    const g = new THREE.ExtrudeGeometry(s, bev ? { depth: mm(depth - 2 * bev), bevelEnabled: true, bevelThickness: mm(bev), bevelSize: mm(bev), bevelSegments: 2 } : { depth: mm(depth), bevelEnabled: false });
    const o = new THREE.Mesh(g, m); o.position.z = mm(z + bev); o.castShadow = o.receiveShadow = true; o.userData.col = col; group.add(o); return o;
  }
  // quadrilatère quelconque vu de face (traverse inclinée d'un cadre)
  function quad(x0, x1, b0, b1, t0, t1, depth, m, z, col) {
    const s = new THREE.Shape([[x0, b0], [x1, b1], [x1, t1], [x0, t0]].map(p => new THREE.Vector2(mm(p[0]), mm(p[1]))));
    const o = new THREE.Mesh(new THREE.ExtrudeGeometry(s, { depth: mm(depth), bevelEnabled: false }), m);
    o.position.z = mm(z); o.castShadow = o.receiveShadow = true; o.userData.col = col; group.add(o); return o;
  }
  function cyl(r, len, m, x, y, z, axis, col) {
    const o = new THREE.Mesh(new THREE.CylinderGeometry(mm(r), mm(r), mm(len), 20), m);
    if (axis === 'x') o.rotation.z = Math.PI / 2; if (axis === 'z') o.rotation.x = Math.PI / 2;
    o.position.set(mm(x), mm(y), mm(z)); o.castShadow = true; o.userData.col = col; group.add(o); return o;
  }
  function label(text, x, y, z, size = 70) {
    const t = labelTex(text), sp = new THREE.Sprite(M(new THREE.SpriteMaterial({ map: t, depthTest: false, transparent: true })));
    sp.scale.set(mm(size) * t.userData.aspect, mm(size), 1); sp.position.set(mm(x), mm(y), mm(z)); sp.renderOrder = 10; group.add(sp);
  }
  function line(pts, m) { const g = new THREE.BufferGeometry().setFromPoints(pts.map(p => new THREE.Vector3(mm(p[0]), mm(p[1]), mm(p[2])))); const l = new THREE.Line(g, m); l.renderOrder = 9; group.add(l); }

  // Escalier plein autour du meuble : dessous incliné posé sur le meuble, marches, limon de face, garde-corps
  function buildStair(st, D) {
    if (SHAPE === 'droit') return;
    const n = Math.max(4, Math.round((st.H - st.h) / 185)), run = (st.L - st.F) / n, r = (st.H - st.h) / n;
    if (r < 60) return;
    const s = r + 170, X = x => (st.dir === 'droite' ? x : st.L - x);
    const tops = [[st.F, st.H + s]];
    let k = 1, xEnd = st.F;
    for (; k < 80; k++) {
      const x = st.F + (k - 1) * run, y = st.H + s - k * r;
      if (y <= 25) { tops.push([x, 0]); xEnd = x; break; }
      tops.push([x, y], [x + run, y]);
    }
    xEnd = Math.max(xEnd, st.L);
    const pts = [[0, st.H], [st.F, st.H], [st.L, st.h], [st.L, 0], [xEnd, 0], ...tops.slice(0, -1).reverse(), [0, st.H + s]];
    const shape = new THREE.Shape(pts.map(p => new THREE.Vector2(mm(X(p[0])), mm(p[1]))));
    const g = new THREE.ExtrudeGeometry(shape, { depth: mm(D + 60), bevelEnabled: false });
    const mLimon = M(new THREE.MeshStandardMaterial({ color: STAIR.limon, roughness: 0.7, envMapIntensity: 0.4 }));
    const mMarche = STAIR.marche === 'marbre' ? M(new THREE.MeshStandardMaterial({ map: marbleTex(), roughness: 0.2, envMapIntensity: 0.8 }))
      : M(new THREE.MeshStandardMaterial({ color: '#ffffff', map: woodTex(STAIR.marche), roughness: 0.5, envMapIntensity: 0.5 }));
    const stair = new THREE.Mesh(g, [mLimon, mMarche]); stair.position.z = mm(-20); stair.castShadow = stair.receiveShadow = true; group.add(stair);
    // limon plein (photos Galerie, Coulisse, Comptoir, Salon) : une bande droite qui cache le profil des marches
    const yN = x => st.H + s - (x - st.F) / run * r;
    if (STAIR.closed) {
      const band = [[0, st.H], [st.F, st.H], [st.L, st.h], [st.L, 0], [xEnd, 0], [xEnd, Math.max(130, yN(xEnd) + 130)], [st.F, st.H + s + 130], [0, st.H + s + 130]];
      const bs = new THREE.Shape(band.map(p => new THREE.Vector2(mm(X(p[0])), mm(p[1]))));
      const b = new THREE.Mesh(new THREE.ExtrudeGeometry(bs, { depth: mm(30), bevelEnabled: false }), mLimon); b.position.z = mm(D + 40); b.castShadow = b.receiveShadow = true; group.add(b);
    }
    // nez de marche légèrement débordant, pour lire chaque marche comme sur les photos
    for (let j = 1; j < k; j++) {
      const x0 = st.F + (j - 1) * run, y = st.H + s - j * r; if (y <= 25) break;
      const xa = Math.min(X(x0), X(x0 + run + 25)), xb = Math.max(X(x0), X(x0 + run + 25));
      box(xb - xa, 30, D + 85, mMarche, xa, y - 30 + 1, -20);
    }
    // ruban LED sous le limon, le long de la pente (photo du Comptoir)
    if (STAIR.led) {
      const len = Math.hypot(st.L - st.F, st.H - st.h), strip = new THREE.Mesh(new THREE.BoxGeometry(mm(len), mm(14), mm(10)), M(new THREE.MeshBasicMaterial({ color: '#fff0d0', toneMapped: false })));
      strip.position.set(mm(X((st.F + st.L) / 2)), mm((st.H + st.h) / 2 + 12), mm(D + 36)); strip.rotation.z = (st.dir === 'droite' ? -1 : 1) * Math.atan2(st.H - st.h, st.L - st.F); group.add(strip);
    }
    // garde-corps
    const yAt = x => st.H + s - (x - st.F) / run * r;
    const xa = st.F, xb = Math.max(st.F + run, xEnd - run);
    if (STAIR.rampe === 'verre') {
      const lift = STAIR.closed ? 110 : 0;
      const gs = new THREE.Shape([[xa, yAt(xa) + lift], [xb, yAt(xb) + lift], [xb, yAt(xb) + lift + 900], [xa, yAt(xa) + lift + 900]].map(p => new THREE.Vector2(mm(X(p[0])), mm(p[1]))));
      const glass = new THREE.Mesh(new THREE.ExtrudeGeometry(gs, { depth: mm(10), bevelEnabled: false }),
        M(new THREE.MeshPhysicalMaterial({ color: '#e6efed', roughness: 0.05, transmission: 0.92, thickness: 0.01, transparent: true, opacity: 0.25, envMapIntensity: 1 })));
      glass.position.z = mm(STAIR.closed ? D + 52 : D + 20); group.add(glass);
    } else if (STAIR.rampe && STAIR.rampe.startsWith('balustres')) {
      const mB = M(new THREE.MeshStandardMaterial({ color: STAIR.rampe.endsWith('noir') ? '#1d1d1d' : '#F2F0EB', roughness: 0.5, metalness: STAIR.rampe.endsWith('noir') ? 0.4 : 0 }));
      const mR = M(new THREE.MeshStandardMaterial({ color: '#ffffff', map: woodTex(STAIR.marche === 'marbre' ? '#7A5235' : STAIR.marche), roughness: 0.45 }));
      for (let j = 1; j < k - 1; j++) {
        const xc = st.F + (j - 0.5) * run, y0 = st.H + s - j * r; if (y0 <= 30) break;
        cyl(STAIR.rampe.endsWith('noir') ? 8 : 18, yAt(xc) + 880 - y0, mB, X(xc), y0 + (yAt(xc) + 880 - y0) / 2, D + 10);
      }
      const len = Math.hypot(xb - xa, yAt(xa) - yAt(xb)), rail = new THREE.Mesh(new THREE.BoxGeometry(mm(len), mm(55), mm(70)), mR);
      rail.position.set(mm(X((xa + xb) / 2)), mm((yAt(xa) + yAt(xb)) / 2 + 900), mm(D + 10));
      rail.rotation.z = (st.dir === 'droite' ? -1 : 1) * Math.atan2(yAt(xa) - yAt(xb), xb - xa); rail.castShadow = true; group.add(rail);
    }
  }

  // Poignées : barre (verticale pour les portes, horizontale pour les tiroirs) ou bouton
  function handleAt(x, y, z, orient, len, mMetal, col) {
    const h = handleOf(S.handle); if (!h.c) return [];
    if (h.knob) {
      const k = new THREE.Mesh(new THREE.SphereGeometry(mm(15), 20, 14), mMetal); k.scale.z = 0.8; k.position.set(mm(x), mm(y), mm(z + 26)); k.castShadow = true; k.userData.col = col; group.add(k);
      return [k, cyl(5, 22, mMetal, x, y, z + 11, 'z', col)];
    }
    const L = Math.max(90, len);
    const bar = cyl(6, L, mMetal, x, y, z + 32, orient === 'v' ? 'y' : 'x', col);
    const off = L / 2 - 18, parts = [bar];
    [-off, off].forEach(o => parts.push(cyl(4, 28, mMetal, orient === 'v' ? x : x + o, orient === 'v' ? y + o : y, z + 16, 'z', col)));
    return parts;
  }
  // Façade (porte, tiroir, placard) : lisse ou à cadre ; top suit la pente si yTop est null
  function frontPanel(xa, xb, yb, ta, tb, m, z, col) {
    const parts = [];
    if (S.style !== 'cadre') { parts.push(slab(xa, xb, yb, ta, tb, 19, m, z, col, 1.5)); return parts; }
    parts.push(slab(xa, xb, yb, ta, tb, 10, m, z, col, 1)); // panneau central en retrait
    const fw = Math.min(70, (xb - xa) / 5), z2 = z + 8.5, d = 11.5; // le cadre mord dans le panneau : pas de faces superposées
    parts.push(slab(xa, xa + fw, yb, ta, ta + (tb - ta) * fw / (xb - xa), d, m, z2, col, 1));
    parts.push(slab(xb - fw, xb, yb, tb - (tb - ta) * fw / (xb - xa), tb, d, m, z2, col, 1));
    parts.push(slab(xa + fw, xb - fw, yb, yb + fw, yb + fw, d, m, z2, col, 1));
    const tA = ta + (tb - ta) * fw / (xb - xa), tB = tb - (tb - ta) * fw / (xb - xa);
    if (Math.min(tA, tB) - yb > 2 * fw + 40) parts.push(quad(xa + fw, xb - fw, tA - fw, tB - fw, tA, tB, d, m, z2, col));
    return parts;
  }
  function pivotOpen(parts, hingeX, D, ang) {
    const piv = new THREE.Group(); piv.position.set(mm(hingeX), 0, mm(D + 20)); group.add(piv);
    parts.forEach(p => { p.position.x -= piv.position.x; p.position.z -= piv.position.z; piv.add(p); }); piv.rotation.y = ang;
  }

  let lights = [];
  function build() {
    dirty = true;
    group.traverse(o => o.geometry && o.geometry.dispose());
    while (group.children.length) group.remove(group.children[0]);
    mats.splice(0).forEach(m => m.dispose());
    const st = S, base = baseH(st), cs = columns(st), D = st.D;
    const mBody = finish(st.body), mFront = finish(st.front), mIn = finish(st.inside), mShelfC = finish(st.shelfc), mTop = topMat(st);
    const fr = frameOf(st.frame), mFrame = fr.id === 'front' ? mFront : M(new THREE.MeshStandardMaterial({ color: fr.c, roughness: fr.metal ? 0.3 : 0.5, metalness: fr.metal ? 0.9 : 0.2 }));
    const mGlass = M(new THREE.MeshPhysicalMaterial({ color: '#e3ebe8', roughness: 0.03, transmission: 0.95, thickness: 0.006, transparent: true, opacity: 0.22, envMapIntensity: 1.2 }));
    const hc = handleOf(st.handle).c, mMetal = M(new THREE.MeshStandardMaterial({ color: hc || '#888', metalness: 0.92, roughness: 0.32 }));
    const mRod = M(new THREE.MeshStandardMaterial({ color: '#C4C5C7', metalness: 0.95, roughness: 0.25 }));
    const mDark = M(new THREE.MeshStandardMaterial({ color: '#2a2725', metalness: 0.6, roughness: 0.4 }));
    const mGlow = M(new THREE.MeshBasicMaterial({ map: glowTex, transparent: true, depthWrite: false, toneMapped: false }));

    // cadrage de la lumière principale sur le meuble
    sun.position.set(mm(st.L / 2) - 3, 6, 5.5); sun.target.position.set(mm(st.L / 2), mm(st.H / 2), 0);
    const sc = sun.shadow.camera; Object.assign(sc, { left: -mm(st.L) / 2 - 3, right: mm(st.L) / 2 + 3, top: 5, bottom: -3, near: 0.5, far: 20 }); sc.updateProjectionMatrix();
    lights.forEach(l => scene.remove(l)); lights = [];

    buildStair(st, D);
    // socle
    if (st.socle === 'plinthe') box(st.L - 30, base, D - 70, M(new THREE.MeshStandardMaterial({ color: colorOf(st.body).c, roughness: 0.8 })), 15, 0, 25);
    if (st.socle === 'pieds') cs.forEach(c => [c.x0 + 40, c.x1 - 40].forEach(x => [60, D - 60].forEach(z => {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.009, mm(base), 16), mDark); leg.position.set(mm(x), mm(base / 2), mm(z)); leg.castShadow = true; group.add(leg);
    })));
    box(st.L, T, D, mBody, 0, base, 0);
    if (st.socle === 'plinthe' && st.ledSocle) { // filet de lumière sous le socle, qui éclaire le sol
      box(st.L - 60, 5, 8, M(new THREE.MeshBasicMaterial({ color: '#ffe2b0' })), 30, base - 8, D - 52);
      const g = new THREE.Mesh(new THREE.PlaneGeometry(mm(st.L - 40), 0.24), mGlow); g.rotation.x = -Math.PI / 2; g.position.set(mm(st.L / 2), 0.002, mm(D - 45) + 0.12); group.add(g);
    }
    // niches fusionnées : même hauteur d'étagères sur toute la niche
    const mergeP = cs.map((c, i) => mergedWithPrev(st, cs, i)), runTop = cs.map(c => c.hmax - T);
    for (let i = 1; i < cs.length; i++) if (mergeP[i]) runTop[i] = Math.max(runTop[i], runTop[i - 1]);
    for (let i = cs.length - 2; i >= 0; i--) if (mergeP[i + 1]) runTop[i] = Math.max(runTop[i], runTop[i + 1]);

    cs.forEach(c => {
      const k = c.k, i = c.i, lay = layout(st, c);
      // caisson : montants (coupés au plan de travail si la niche est d'un seul tenant), dessus incliné, fond
      if (mergeP[i]) slab(c.x0, c.x0 + T, base, lay.counterY, lay.counterY, D, mBody, 0, i);
      else slab(c.x0, c.x0 + T, base, c.h0, hAt(st, c.x0 + T), D, mBody, 0, i);
      if (i === cs.length - 1) slab(c.x1 - T, c.x1, base, hAt(st, c.x1 - T), c.h1, D, mBody, 0, i);
      const len = Math.hypot(c.w, c.h1 - c.h0), top = new THREE.Mesh(new THREE.BoxGeometry(mm(len), mm(T), mm(D)), mBody);
      top.position.set(mm((c.x0 + c.x1) / 2), mm((c.h0 + c.h1) / 2 - T / 2), mm(D / 2)); top.rotation.z = Math.atan2(c.h1 - c.h0, c.w); top.castShadow = top.receiveShadow = true; top.userData.col = i; group.add(top);
      if (st.back) slab(c.x0, c.x1, base, c.h0 - T, c.h1 - T, 8, mIn, 0, i);

      const ix0 = c.x0 + T, ix1 = (i === cs.length - 1 ? c.x1 - T : c.x1), iw = ix1 - ix0;
      const slideOut = open && k.facade === 'coulissant' && i === sel ? 450 : 0;
      // habillage intérieur des niches visibles (côtés), dans la couleur « intérieur »
      const openZones = [];
      if (k.facade === 'ouvert' || k.facade === 'verre') openZones.push([lay.yD, lay.counterY !== null ? lay.counterY : null]);
      if (lay.counterY !== null) openZones.push([lay.counterY + 30, lay.upper ? lay.upper.y0 : null]);
      if (lay.upper && lay.upper.type === 'verre') openZones.push([lay.upper.y0, null]);
      if (st.inside !== st.body) openZones.forEach(([y0, y1]) => {
        const ya = (x) => (y1 === null ? hAt(st, x) - T : y1), niche = lay.counterY !== null && y0 === lay.counterY + 30;
        if (!(niche && mergeP[i])) slab(ix0, ix0 + 3, y0, ya(ix0), ya(ix0 + 3), D - 12, mIn, 10, i);
        if (!(niche && mergeP[i + 1])) slab(ix1 - 3, ix1, y0, ya(ix1 - 3), ya(ix1), D - 12, mIn, 10, i);
      });

      // tiroirs (remplissent tout le bas quand la colonne est ouverte sous un plan de travail)
      for (let j = 0; j < k.drawers; j++) {
        const yy = lay.yb + j * lay.dh, out = open ? 300 : 0;
        const parts = frontPanel(ix0 + 2, ix1 - 2, yy + 2, yy + lay.dh - 2, yy + lay.dh - 2, mFront, D + out, i);
        box(iw - 40, lay.dh - 50, D - 50, mIn, ix0 + 20, yy + 12, 25 + out, i);
        handleAt((ix0 + ix1) / 2, yy + lay.dh / 2 + (handleOf(st.handle).knob ? 0 : lay.dh * 0.22), D + 19 + out, 'h', Math.min(320, iw * 0.42), mMetal, i);
      }
      if (k.drawers && !lay.fill) box(iw, T, D - 10, mBody, ix0, lay.yD - T, 0, i);
      // plan de travail, niche et placard haut
      if (lay.counterY !== null) box(c.w + (i === cs.length - 1 ? 0 : 2), 30, D + 22, mTop, c.x0, lay.counterY, 0, i);
      if (lay.upper) box(iw, T, D - 10, mBody, ix0, lay.upper.y0, 0, i);
      // penderie
      let shelfTop = lay.upper ? lay.upper.y0 : lay.counterY !== null ? runTop[i] : c.hmax - T;
      if (k.rod && shelfTop - lay.yD > 700) {
        const ry = Math.min(shelfTop, c.hmin - T) - 70;
        cyl(12.5, iw - 10, mRod, ix0 + iw / 2, ry, D / 2 + slideOut, 'x', i);
        shelfTop = Math.max(lay.yD + 150, ry - 1000);
      }
      // étagères : réparties sur la hauteur, raccourcies là où la pente passe (comme sur les photos)
      const mShelf = k.glassShelves ? mGlass : mShelfC;
      const zones = lay.counterY !== null ? [[lay.yD, lay.counterY], [lay.counterY + 30, shelfTop]] : [[lay.yD, shelfTop]];
      let left = k.shelves; const shelfYs = [];
      zones.forEach((z, zi) => {
        const closedLow = ['porte', 'double', 'verre'].includes(k.facade);
        const n = zones.length === 1 ? left : zi === 0 ? (lay.fill ? 0 : closedLow ? Math.min(1, left) : Math.floor(left / 2)) : left;
        left -= n;
        const niche = lay.counterY !== null && zi === 1, h = z[1] - z[0], ys = [];
        if (niche && n > 0) { // au-dessus du plan : grand espace libre pour la machine à café, puis les étagères
          const first = Math.max(h / (n + 1), Math.min(600, h - n * 220)), g2 = (h - first) / (n + 0.4);
          for (let j = 0; j < n; j++) ys.push(z[0] + first + j * g2);
        } else for (let j = 1; j <= n; j++) ys.push(z[0] + j * h / (n + 1));
        const sx0 = niche && mergeP[i] ? c.x0 : ix0, sx1 = niche && mergeP[i + 1] ? c.x1 : ix1;
        // séparations verticales : la niche devient une grille de casiers aux étagères décalées (photo Galerie)
        const div = (niche || (zones.length === 1 && k.facade === 'ouvert')) ? (k.div || 0) : 0;
        const cells = Array.from({ length: div + 1 }, (_, j) => [sx0 + (sx1 - sx0) * j / (div + 1) + (j ? T / 2 : 0), sx0 + (sx1 - sx0) * (j + 1) / (div + 1) - (j < div ? T / 2 : 0)]);
        for (let j = 1; j <= div; j++) {
          const xd = sx0 + (sx1 - sx0) * j / (div + 1), top = x => (lay.upper ? lay.upper.y0 : hAt(st, x) - T);
          if (top(xd) - z[0] > 120) slab(xd - T / 2, xd + T / 2, z[0], top(xd - T / 2), top(xd + T / 2), D - 30, mBody, 10, i);
        }
        const step = ys.length > 1 ? ys[1] - ys[0] : h / 2;
        cells.forEach((cell, j) => ys.forEach(y0 => {
          const yy = y0 + (j % 2 ? step * 0.45 : 0); if (yy > z[1] - 60) return;
          const sp = lay.upper && yy > lay.upper.y0 ? cell : spanUnder(st, yy, cell[0], cell[1], T + 20);
          if (!sp) return;
          box(sp[1] - sp[0], k.glassShelves ? 8 : 30, D - 40, mShelf, sp[0], yy, 10 + slideOut, i);
          shelfYs.push([yy, sp]);
        }));
      });
      if (lay.upper) box(iw, T, D - 40, mShelfC, ix0, (lay.upper.y0 + c.hmin) / 2, 10, i);
      // LED : halo chaud sous chaque étagère et sous le plafond de la niche, plus une vraie source de lumière
      if (k.led) {
        const ceil = lay.upper ? lay.upper.y0 : null;
        // chaque case éclairée : lumière vive sous l'étagère qui s'éteint vers le bas de la case
        const floors = [[lay.yD, [c.x0, c.x1]], ...(lay.counterY !== null ? [[lay.counterY + 30, [c.x0, c.x1]]] : []), ...shelfYs.map(s => [s[0] + 30, s[1]])];
        const glowAt = (y, sp) => { // plancher de la case = étagère juste en dessous dans le même casier
          const fl = Math.max(...floors.filter(f => f[0] < y - 40 && f[1][0] < sp[1] - 20 && f[1][1] > sp[0] + 20).map(f => f[0])), h = Math.min(900, y - fl); if (!(h >= 60)) return;
          const g = new THREE.Mesh(new THREE.PlaneGeometry(mm(sp[1] - sp[0]), mm(h)), mGlow);
          g.position.set(mm((sp[0] + sp[1]) / 2), mm(y - h / 2 - 2), mm(9.5)); group.add(g);
        };
        shelfYs.forEach(([y, sp]) => glowAt(y, sp));
        const nx0 = lay.counterY !== null && mergeP[i] ? c.x0 : ix0, nx1 = lay.counterY !== null && mergeP[i + 1] ? c.x1 : ix1;
        if (ceil) glowAt(ceil, [nx0, nx1]);
        else if (!(lay.counterY !== null && (mergeP[i] || mergeP[i + 1]))) { const yTop = c.hmin - T - 10, sp = spanUnder(st, yTop - 40, nx0, nx1, 40) || [nx0, nx1]; glowAt(yTop, sp); }
        if (lights.length < 6) { const L = new THREE.PointLight('#ffcf94', 0.55, 1.6, 2); L.position.set(mm(ix0 + iw / 2), mm(Math.min(c.hmin - 120, (ceil || c.hmin) - 80)), mm(D - 140)); scene.add(L); lights.push(L); }
      }

      // façades : porte, double porte, vitrée (1 ou 2 vantaux) ; yTop null = suit la pente
      const facadeAt = (type, yBot, yTop, handleY) => {
        const n = leaves(type, c.w), m = type === 'verre' ? mGlass : mFront;
        const topAt = x => (yTop === null ? hAt(st, x) - 6 : yTop - 3);
        for (let j = 0; j < n; j++) {
          const xa = c.x0 + (c.w * j) / n, xb = c.x0 + (c.w * (j + 1)) / n, hinge = n === 2 ? (j === 0 ? 'g' : 'd') : k.hinge;
          const ta = topAt(xa), tb = topAt(xb);
          let parts;
          if (type === 'verre') { // vitre dans un cadre fin
            parts = [slab(xa + 2, xb - 2, yBot, ta, tb, 6, mGlass, D + 6, i)];
            const fw = 34, t1 = ta + (tb - ta) * fw / (xb - xa), t2 = tb - (tb - ta) * fw / (xb - xa);
            parts.push(slab(xa + 2, xa + 2 + fw, yBot, ta, t1, 19, mFrame, D, i, 1), slab(xb - 2 - fw, xb - 2, yBot, t2, tb, 19, mFrame, D, i, 1),
              slab(xa + 2 + fw, xb - 2 - fw, yBot, yBot + fw, yBot + fw, 19, mFrame, D, i, 1),
              quad(xa + 2 + fw, xb - 2 - fw, t1 - fw, t2 - fw, t1, t2, 19, mFrame, D, i));
          } else parts = frontPanel(xa + 2, xb - 2, yBot, ta, tb, m, D, i);
          const hx = hinge === 'g' ? xb - 45 : xa + 45;
          parts.push(...handleAt(hx, handleY, D + 19, 'v', Math.min(420, (Math.min(ta, tb) - yBot) * 0.3), mMetal, i));
          if (open) pivotOpen(parts, hinge === 'g' ? xa : xb, D, hinge === 'g' ? -1.75 : 1.75);
        }
      };
      const fy = k.drawers && !lay.fill ? lay.yD - T + 2 : base + 2;
      if (['porte', 'double', 'verre'].includes(k.facade)) {
        if (lay.counterY !== null) facadeAt(k.facade, fy, lay.counterY, Math.max(fy + 60, lay.counterY - 150));
        else facadeAt(k.facade, fy, null, Math.min(fy + 1000, fy + (c.hmin - fy) * 0.5));
      }
      if (lay.upper) facadeAt(lay.upper.type, lay.upper.y0, null, lay.upper.y0 + 90);
      if (k.facade === 'coulissant') {
        frontPanel(c.x0 + 2, c.x1 - 2, base + 2, hAt(st, c.x0) - 6, hAt(st, c.x1) - 6, mFront, D + slideOut, i);
        handleAt(c.x0 + c.w / 2, Math.min(base + 1000, c.hmin * 0.5), D + 19 + slideOut, 'v', 300, mMetal, i);
      }
      // sélection
      if (tab === 2 && i === sel) {
        const g = M(new THREE.LineBasicMaterial({ color: '#B39360', depthTest: false, transparent: true }));
        line([[c.x0, base, D + 45], [c.x1, base, D + 45], [c.x1, c.h1, D + 45], [c.x0, c.h0, D + 45], [c.x0, base, D + 45]], g);
      }
      if (showCotes()) label(Math.round(c.w / 10) + ' cm', (c.x0 + c.x1) / 2, 60, D + 160, 95);
    });
    // cotes générales
    if (showCotes()) {
      const mL = M(new THREE.LineBasicMaterial({ color: '#16130F', depthTest: false, transparent: true, opacity: 0.55 }));
      line([[0, -1, D + 340], [st.L, -1, D + 340]], mL); label(cmf(st.L) + ' cm', st.L / 2, 2, D + 340, 130);
      const xh = SHAPE === 'droit' || st.dir === 'droite' ? -200 : st.L + 200, xl = SHAPE === 'droit' || st.dir === 'droite' ? st.L + 200 : -200;
      line([[xh, 0, D], [xh, st.H, D]], mL); label(cmf(st.H) + ' cm', xh, st.H / 2, D, 130);
      if (SHAPE !== 'droit') { line([[xl, 0, D], [xl, st.h, D]], mL); label(cmf(st.h) + ' cm', xl, st.h / 2, D, 130); }
    }
  }

  // Caméra : vue de face légèrement de biais, à hauteur d'œil, comme les photos
  function fit() {
    dirty = true;
    const w = mm(S.L), h = mm(S.H), t = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    controls.target.set(w / 2, h * 0.48, mm(S.D) / 2);
    const dist = Math.max((h * 0.74) / t, (w * 0.68) / (t * camera.aspect)) + mm(S.D);
    if (frontView) { // vue de face, bien droite : on lit la façade comme sur un plan
      controls.target.set(w / 2, h / 2, mm(S.D) / 2);
      camera.position.set(w / 2, h / 2, mm(S.D) / 2 + dist * 1.05); controls.update(); return;
    }
    const az = S.dir === 'droite' ? 0.22 : -0.22;
    camera.position.set(w / 2 + Math.sin(az) * dist, Math.min(1.55, h * 0.62) + 0.15, mm(S.D) / 2 + Math.cos(az) * dist); controls.update();
  }
  let frontView = false;
  let composer = null, aoReady = false;
  let fitted = false;
  function resize() {
    const r = host.getBoundingClientRect(); if (!r.width) return;
    renderer.setSize(r.width, r.height); camera.aspect = r.width / Math.max(1, r.height); camera.updateProjectionMatrix();
    if (composer) composer.setSize(r.width, r.height);
    dirty = true;
    if (!fitted || !userMoved) { fitted = true; fit(); } // recadre tant que le client n'a pas tourné la vue
  }
  let userMoved = false;
  controls.addEventListener('start', () => (userMoved = true));
  new ResizeObserver(resize).observe(host);
  // Ombres de contact (occlusion ambiante) sur ordinateur, chargées à part : si elles échouent, la 3D reste affichée
  if (window.innerWidth > 900) {
    const P = 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/postprocessing/';
    Promise.all(['EffectComposer', 'RenderPass', 'GTAOPass', 'OutputPass'].map(n => import(P + n + '.js/+esm'))).then(([E, R, G, O]) => {
      const r = host.getBoundingClientRect();
      composer = new E.EffectComposer(renderer);
      composer.addPass(new R.RenderPass(scene, camera));
      const ao = new G.GTAOPass(scene, camera, r.width, r.height);
      ao.updateGtaoMaterial({ radius: 0.35, distanceExponent: 1.4, thickness: 1.2, scale: 1.1, samples: 16 });
      ao.blendIntensity = 0.9; composer.addPass(ao);
      composer.addPass(new O.OutputPass());
      composer.setSize(r.width, r.height); aoReady = true; dirty = true;
    }).catch(() => { composer = null; });
  }
  const ray = new THREE.Raycaster(), ptr = new THREE.Vector2(); let down = null;
  renderer.domElement.addEventListener('pointerdown', e => (down = [e.clientX, e.clientY]));
  renderer.domElement.addEventListener('pointerup', e => {
    if (!down || Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 6) return;
    const r = renderer.domElement.getBoundingClientRect();
    ptr.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ptr, camera);
    const hit = ray.intersectObjects(group.children, true).find(h => h.object.userData.col !== undefined);
    if (hit) { sel = hit.object.userData.col; tab = 2; render(); }
  });
  let lastFit = '';
  (function loop() {
    requestAnimationFrame(loop);
    const key = S.L + ':' + S.H + ':' + S.dir; if (fitted && !dragSnap && key !== lastFit) { lastFit = key; fit(); }
    if (controls.update()) dirty = true;
    if (!dirty) return;
    dirty = false;
    if (composer && aoReady) composer.render(); else renderer.render(scene, camera);
  })();
  normalize(); baseKey = JSON.stringify(S); render(); resize();
  // Mesures tapées sur le croquis de la page d'accueil (?L=3300&F=450&H=2600&h=750&D=600, en mm)
  const q = new URLSearchParams(location.search), keys = ['L', 'F', 'H', 'h', 'D'];
  if (keys.some(k => +q.get(k) > 0)) commit(() => keys.forEach(k => { const v = +q.get(k); if (v > 0) applyRange(k, v); }));
  // Projet reçu par e-mail (?p=…) : le client retrouve son meuble tel quel
  const shared = q.get('p') && sharedState(q.get('p'));
  if (shared) commit(() => { S = shared; sel = 0; });
  if (savedProject()) { // un projet enregistré attend : on propose de le reprendre
    const r = document.createElement('button'); r.type = 'button'; r.className = 'nkx-resume'; r.dataset.act = 'restore';
    r.textContent = 'Reprendre mon projet enregistré'; root.querySelector('.nkx-tools').appendChild(r);
  }
}

function injectCss() {
  const s = document.createElement('style');
  s.textContent = `
  .nkx{display:grid;grid-template-columns:1.35fr 1fr;border:1px solid #DDD5C8;background:#fff;font-family:var(--nk-sans,Inter,sans-serif);color:#16130F}
  .nkx *{box-sizing:border-box}
  .nkx button{font-family:inherit;color:inherit}
  .nkx button:disabled{opacity:.35;cursor:not-allowed}
  .nkx-view{position:relative;height:680px;background:#EEE9E1}
  .nkx-canvas{position:absolute;inset:0}.nkx-canvas canvas{display:block;width:100%!important;height:100%!important;cursor:grab;touch-action:pan-y}
  .nkx-away{margin:10px 0 0;font-size:13px;line-height:1.5;color:#5E574E}
  .nkx-tools{position:absolute;top:14px;left:14px;right:14px;display:flex;flex-wrap:wrap;gap:6px}
  .nkx-tools button{background:rgba(255,255,255,.94);border:1px solid #DDD5C8;padding:8px 12px;font-size:12px;font-weight:500;cursor:pointer;min-height:36px}
  .nkx-tools button.is-on{background:#16130F;color:#fff;border-color:#16130F}
  .nkx-tools [data-act=undo],.nkx-tools [data-act=redo]{font-size:16px;padding:6px 11px}
  .nkx-hint{position:absolute;bottom:12px;left:0;right:0;text-align:center;font-size:12px;color:#5E574E;margin:0;pointer-events:none}
  .nkx-panel{display:flex;flex-direction:column;border-left:1px solid #DDD5C8;height:680px;min-width:0}
  .nkx-tabs{display:flex;border-bottom:1px solid #DDD5C8}
  .nkx-tabs button{flex:1;background:none;border:0;border-bottom:2px solid transparent;padding:16px 8px 14px;font-size:12px;font-weight:500;color:#5E574E!important;cursor:pointer;text-align:left}
  .nkx-tabs b{display:block;font:500 20px/1 var(--nk-serif,Georgia);margin-bottom:4px}
  .nkx-tabs .is-on{color:#16130F!important;border-color:#16130F}
  .nkx-body{flex:1;min-height:0;padding:26px 28px;overflow:auto}
  .nkx .nkx-body h3{font:500 26px/1.15 var(--nk-serif,Georgia);margin:0 0 8px}
  .nkx .nkx-help{font-size:13px;color:#5E574E;line-height:1.6;margin:0 0 18px}
  .nkx .nkx-sub{font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;margin:22px 0 10px}
  .nkx-schema{width:100%;max-width:320px;display:block;margin:0 0 16px}.nkx-schema text{font:600 13px Inter,sans-serif;fill:#6E5236}.nkx-schema path{stroke-width:1.2}
  .nkx-seg{display:flex;gap:8px}.nkx-seg--wrap{flex-wrap:wrap}
  .nkx-seg button,.nkx-chips button,.nkx-opts button,.nkx-step button{background:#fff;border:1px solid #DDD5C8;cursor:pointer;font-size:13px;font-weight:500}
  .nkx-seg button{padding:11px 14px;min-height:44px;display:inline-flex;align-items:center;gap:8px}.nkx-seg .is-on,.nkx-chips .is-on,.nkx-opts .is-on{border-color:#16130F;box-shadow:inset 0 0 0 1px #16130F}
  .nkx-dot{width:16px;height:16px;border-radius:50%;border:1px solid rgba(0,0,0,.15);flex:none}
  .nkx-step{display:flex;align-items:center;gap:14px}.nkx-step button{width:44px;height:44px;font-size:18px}.nkx-step b{font:500 24px var(--nk-serif,Georgia);min-width:24px;text-align:center}.nkx-step span{font-size:13px;color:#5E574E}
  .nkx-chips{display:flex;gap:6px;flex-wrap:wrap;margin:6px 0 18px}.nkx-chips button{min-width:46px;height:50px;font:500 18px/1 var(--nk-serif,Georgia);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px}
  .nkx-chips small{font:500 10px var(--nk-sans,Inter);color:#6B645B}
  .nkx-opts{display:grid;grid-template-columns:repeat(5,1fr);gap:6px}.nkx-opts button{padding:10px 4px;display:flex;flex-direction:column;align-items:center;gap:6px;font-size:11px;line-height:1.2}.nkx-opts svg{width:34px;height:34px}
  .nkx-check{display:flex;gap:10px;align-items:center;font-size:14px;margin-top:12px;cursor:pointer;min-height:28px}.nkx-check input{accent-color:#16130F;width:18px;height:18px;flex:none}.nkx-check em{margin-left:auto;font-style:normal;font-size:13px;color:#6E5236;white-space:nowrap}
  .nkx-row{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-top:20px}
  .nkx-link{background:none;border:0;padding:0;font-size:13px;text-decoration:underline;cursor:pointer}
  .nkx-next{margin-top:26px;background:#16130F;color:#fff!important;border:0;padding:15px 22px;font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;cursor:pointer}
  .nkx-sw{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
  .nkx-sw button{background:#fff;border:1px solid #DDD5C8;padding:8px 6px;cursor:pointer;display:flex;align-items:center;gap:8px;font-size:12px;line-height:1.25;text-align:left}
  .nkx-sw i{width:30px;height:30px;border-radius:50%;border:1px solid rgba(0,0,0,.12);flex:none}.nkx-sw em{display:block;font-style:normal;color:#6E5236;font-size:10px;letter-spacing:.06em;text-transform:uppercase}
  .nkx-sw .is-on{border-color:#16130F;box-shadow:inset 0 0 0 1px #16130F}
  .nkx-lines,.nkx-conf{list-style:none;padding:0;margin:0 0 18px}.nkx-lines li{display:flex;justify-content:space-between;gap:16px;padding:11px 0;border-bottom:1px solid #EEE8DF;font-size:14px}.nkx-lines li span{color:#4A443D}.nkx-lines b{white-space:nowrap}
  .nkx-lines .nkx-tot{border-bottom:0;border-top:1px solid #16130F;margin-top:6px;padding-top:16px}.nkx-tot b{font:500 26px var(--nk-serif,Georgia)}
  .nkx-conf li{font-size:13px;line-height:1.5;color:#4A443D;padding:6px 0;border-bottom:1px solid #F1ECE4}
  .nkx-foot{display:flex;justify-content:space-between;align-items:center;gap:16px;padding:18px 28px;background:#16130F;color:#EDE6DB}
  .nkx-foot span{display:block;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#9D9488}.nkx-foot b{display:block;font:500 32px/1.1 var(--nk-serif,Georgia);color:#fff}.nkx-foot small{display:block;font-size:12px;color:#CFC6B8;margin-top:3px}
  .nkx-prow{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap}.nkx-old{font-size:15px;color:#9D9488}.nkx-badge{font-style:normal;font-size:11px;font-weight:600;letter-spacing:.06em;background:#B39360;color:#16130F;padding:4px 7px}
  .nkx-badge:empty,.nkx-old:empty{display:none}
  .nkx-r{margin:0 0 20px}.nkx-r-top{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-bottom:8px;font-size:13px;color:#4A443D}
  .nkx-r-top b{font:500 20px var(--nk-serif,Georgia);color:#16130F;white-space:nowrap}.nkx-r small{display:block;font-size:11px;color:#6B645B;margin-top:6px}
  .nkx-r input{-webkit-appearance:none;appearance:none;width:100%;height:32px;background:transparent;cursor:pointer;touch-action:pan-y;margin:0}
  .nkx-r input::-webkit-slider-runnable-track{height:4px;border-radius:2px;background:linear-gradient(90deg,#16130F var(--p,50%),#DDD5C8 var(--p,50%))}
  .nkx-r input::-moz-range-track{height:4px;border-radius:2px;background:#DDD5C8}.nkx-r input::-moz-range-progress{height:4px;background:#16130F}
  .nkx-r input::-webkit-slider-thumb{-webkit-appearance:none;width:26px;height:26px;margin-top:-11px;border-radius:50%;background:#fff;border:2px solid #16130F;box-shadow:0 2px 6px rgba(0,0,0,.18)}
  .nkx-r input::-moz-range-thumb{width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid #16130F}
  .nkx-r input:focus-visible::-webkit-slider-thumb{outline:3px solid #B39360}
  .nkx-subt span,.nkx-subt b{color:#5E574E!important}.nkx-disc b,.nkx-disc span{color:#6E5236!important}
  .nkx-cta{background:#fff;color:#16130F!important;border:0;padding:16px 24px;font-size:12px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;cursor:pointer;flex:none}
  .nkx-num{display:inline-flex;align-items:center;border:1px solid #DDD5C8;background:#fff}.nkx-num:focus-within{border-color:#16130F}
  .nkx-num input{width:74px;border:0;background:none;padding:7px 4px 7px 10px;font:500 15px var(--nk-sans,Inter,sans-serif);font-variant-numeric:tabular-nums;color:#16130F;text-align:right}
  .nkx-num input:focus{outline:none}
  .nkx-num span{font-size:12px;color:#5E574E;padding:0 10px 0 4px}
  .nkx-guide{display:inline-block;margin:-4px 0 18px;color:#6E5236}
  .nkx-tools .nkx-warnbtn{background:#FFF4E0;border-color:#E2B866;color:#7A4E0B}
  .nkx-tools .nkx-resume{background:#B39360;border-color:#B39360;color:#16130F}
  .nkx-warns{position:absolute;top:60px;left:14px;max-width:min(420px,calc(100% - 28px));background:#fff;border:1px solid #E2B866;padding:14px 16px;font-size:13px;line-height:1.5;z-index:3;box-shadow:0 8px 24px rgba(0,0,0,.12)}
  .nkx-warns ul{margin:8px 0 10px;padding-left:18px;color:#4A443D}.nkx-warns li{margin:4px 0}
  .nkx-toast{position:absolute;left:50%;bottom:40px;transform:translateX(-50%);background:#16130F;color:#fff;padding:10px 16px;font-size:13px;max-width:calc(100% - 28px);text-align:center;z-index:4}
  .nkx-modal{position:fixed;inset:0;z-index:1000;background:rgba(22,19,15,.55);display:flex;align-items:center;justify-content:center;padding:16px;font-family:var(--nk-sans,Inter,sans-serif);color:#16130F}
  .nkx-modal__box{position:relative;background:#fff;max-width:620px;width:100%;max-height:calc(100vh - 32px);overflow:auto;padding:34px 32px 30px}
  .nkx-modal__box h3{font:500 30px/1.1 var(--nk-serif,Georgia);margin:10px 0 14px}
  .nkx-modal__x{position:absolute;top:10px;right:12px;background:none;border:0;font-size:28px;line-height:1;cursor:pointer;color:#5E574E;padding:6px}
  .nkx-modal .nkx-k,.nkx-k{font:600 11px/1 var(--nk-sans,Inter);letter-spacing:.24em;text-transform:uppercase;color:#6E5236;margin:0}
  .nkx-modal .nkx-help{font-size:14px;color:#5E574E;line-height:1.6;margin:14px 0 0}
  .nkx-modal .nkx-next{margin-top:18px}
  .nkx-guide-svg{width:100%;height:auto;display:block;margin:6px 0 10px}
  .nkx-guide-list{margin:0;padding-left:20px;font-size:14px;line-height:1.6;color:#4A443D}.nkx-guide-list li{margin:6px 0}.nkx-guide-list b{color:#16130F}
  .nkx-modal__price{font:500 34px/1.1 var(--nk-serif,Georgia);margin:4px 0 16px}.nkx-modal__price small{display:block;font:400 13px var(--nk-sans,Inter);color:#5E574E;margin-top:4px}
  .nkx-modal .nkx-conf{list-style:none;padding:0;margin:0 0 14px}.nkx-modal .nkx-conf li{font-size:13px;line-height:1.5;color:#4A443D;padding:6px 0;border-bottom:1px solid #F1ECE4}
  .nkx-demo{background:#EDE6DB;padding:12px 14px;font-size:13px;line-height:1.5;margin:0}
  .nkx-modal .nkx-row{align-items:center}
  .nkx [hidden],.nkx-modal [hidden]{display:none!important}
  .nkx-modal__box--wide{max-width:980px}
  .nkx-plan{border:1px solid #DDD5C8;background:#fff;margin:6px 0 16px;padding:8px}.nkx-plan-svg{display:block;margin:0 auto;width:auto;max-width:100%;height:auto;max-height:62vh}
  .nkx-tools [data-act=plan]{background:#16130F;color:#fff;border-color:#16130F}
  @media (max-width:960px){.nkx{grid-template-columns:1fr;--nkx-vh:min(42vh,360px)}.nkx-view{position:sticky;top:0;z-index:6;height:var(--nkx-vh)}.nkx-panel{border-left:0;border-top:1px solid #DDD5C8;height:auto;scroll-margin-top:var(--nkx-vh)}
    .nkx-tools{flex-wrap:nowrap;overflow-x:auto;top:8px;left:8px;right:8px;scrollbar-width:none}.nkx-tools::-webkit-scrollbar{display:none}.nkx-tools button{flex:0 0 auto;min-height:34px}.nkx-tools .nkx-warnbtn,.nkx-tools .nkx-resume{order:-1}.nkx-hint{bottom:6px;font-size:11px;padding:0 8px}.nkx-warns{top:50px}
    .nkx-body{max-height:none;padding:22px 16px}.nkx-foot{position:sticky;bottom:0;z-index:5;padding:10px 16px calc(10px + env(safe-area-inset-bottom,0px))}.nkx-foot b{font-size:24px}.nkx-foot small{font-size:11px;line-height:1.35}.nkx-foot>div{flex:1;min-width:0}.nkx-foot span{display:none}.nkx-foot .nkx-cta{flex:0 0 auto;padding:12px;font-size:11px;letter-spacing:.06em}.nkx-cta{padding:14px 14px}
    .nkx-tabs{position:sticky;top:var(--nkx-vh);z-index:4;background:#fff}.nkx-tabs button{padding:12px 6px 10px;font-size:11px}.nkx-tabs b{font-size:17px}
    .nkx-opts{grid-template-columns:repeat(3,1fr)}.nkx-sw{grid-template-columns:repeat(2,1fr)}.nkx-tools button{padding:7px 9px;font-size:11px} }
  `;
  document.head.appendChild(s);
}
